// CowboyView: the four masked space-cowboys, built from the Godot project's authored Tripo
// characters + Mixamo animation (baked by bake.ts into public/assets/cowboys/*.cby).
//
// Per cowboy: ONE GPU-skinned mesh (body + jet pack + weapon share one skeleton and one atlas),
// one ink outline (same geometry and skeleton), one shadow pass = 3 draws. All scarf tails of
// every cowboy are one cloth draw, all jet flames one instanced draw. LOD1 (meshopt-simplified
// index buffer, same vertices) past 25 m.
//
// Animation is a small allocation-free layered sampler: lower body from locomotion / air / hover
// clips, upper body from the class's aim pose, overrides for roll / melee / reload / death, then
// procedural layers (aim pitch, recoil, delta-wing flight pose, pause lift, tumble for air dodges
// and banking). Loading is async: a cowboy is invisible until its class asset arrives.

import { feelOf } from '../../feel/recoil';
import * as THREE from 'three';
import type { CowboyRS, CowboyViewApi, ViewCtx } from '../contracts';
import { unlitMaterial, outlineMaterial, HAZE } from '../materials';
import { ShieldSkin } from '../fx/shieldskin';
import { PAL, SLOT_COLORS } from '../palette';
import { Blade } from './blades';
import { ClothBatch, Ribbon, WORLD_WIND, type RibbonDef, type V3 } from './cloth';
import { holdOf, reloadCycle, reloadFrame, HoldK, ReloadK, StowK, type Hold, type Pose } from './holds';
import { weaponDef } from '../../../sim/weapons';
import { loadCowboyAsset, accumulate, accumulateStatic, type CowboyAsset, type ClipData } from './asset';
import { BOTTLE_H, Bottle, Tosses, gulpFill } from './whiskey';
import { gaitOf, contactAt, FootLock } from './feet';

const TAU = Math.PI * 2;
// match sim/player.ts: air dash 0.26 s, barrel roll 0.4 s, dive roll 0.56 s
/** weight lane: the ragdoll fling, the lie on the ground and the get-up (sim: KNOCKDOWN 0.7 + GETUP 0.45) */
const KNOCK_FLING = 0.45, KNOCK_LIE = 0.7, KNOCK_GETUP = 0.45;
const _m4 = new THREE.Matrix4();
const DODGE_DUR = 0.26;
const BARREL_DUR = 0.4;
// the hover dodge flip runs a touch past the sim's 0.26 s dash so the full turn reads; the launch burst
const HOVER_FLIP_DUR = 0.36;
const LAUNCH_DUR = 0.6;
/** playtest 3 (Nick): every clip plays a tenth slower (sim/player.ts PACE); the roll and the dash run a tenth longer */
const ANIM_PACE = 0.9;
const ROLL_DUR = 0.9 / ANIM_PACE; // the sim's roll (CONTROLS.md: 8.1 m/s for 1.0 s)
const SLIDE_DUR = 0.7;
/** the air dive (DodgeK 1): the belly-flop pose holds until touchdown, then LandK.Prone carries the slide and get-up */
const DIVE_DUR = 2.5;
const PRONE_GETUP = 0.5;
/** superhero landing: planted for HERO_HOLD, then rises over HERO_RISE (sim plants 0.3 s, recovers by 0.62 s) */
const HERO_HOLD = 0.34;
const HERO_RISE = 0.3;
/** grenade throw: the throw clip's wind-up -> release -> follow-through window (seconds of clip), played over THROW_DUR */
const HERO_CROUCH = false;
const THROW_DUR = 0.62;
// throwAge 0 is the moment the grenade leaves (sim + prediction), so the window opens with the hand already cocked
// above and behind the head (clip 1.8-2.0 s), snaps through the release (~2.08 s, hand 0.65 m ahead) and follows
// through. The old [0.95, 1.85] slice was the clip's idle-to-wind-up and never threw anything.
const THROW_WIN: [number, number] = [1.98, 2.62];
/** how far up the bottle the fist closes (m from the base) */
const BOTTLE_GRIP_Y = 0.1;
/** how much of the swallow pose is on at chug fraction t (in over the lift, out as the arm comes down) */
const chugGulpW = (t: number) => smooth(clamp((t - (CHUG_GULP0 - 0.12)) / 0.12, 0, 1)) * (1 - smooth(clamp((t - CHUG_GULP1) / 0.08, 0, 1)));
/** the bottle's base-to-neck line while he swallows: neck down and back into the mouth, base up in front */
function chugDir(yaw: number, out: THREE.Vector3) {
  return out.set(-Math.sin(yaw) * -0.72 + Math.cos(yaw) * -0.1, -0.62, -Math.cos(yaw) * -0.72 - Math.sin(yaw) * -0.1).normalize();
}
/** the whiskey chug (heal lane): Godot's stim_chug take played at 2x, as Godot did (the 3.3 s take in ~1.65 s, so
 *  the head-back swallow covers the sim's 1 s chug). Beats are fractions of the played clip, measured off the take:
 *  the hand closes at the belt, the swallows run across the head-back hold, the empty leaves after the finish */
const CHUG_SPEED = 2;
export const CHUG_DUR = 3.3 / CHUG_SPEED;
const CHUG_DRAW = 0.06, CHUG_GULP0 = 0.26, CHUG_GULP1 = 0.8, CHUG_TOSS = 0.92;
/** the empty's exit (sim/whiskey.ts Ditch). After the last swallow (FIN_START) the fist follows `keys`
 *  ([s, right, up, forward]: seconds after FIN_START, metres off the right shoulder in the body frame), lets go at
 *  `rel` with velocity `v` (body frame [right, up, forward], m/s) tumbling at `spin` rad/s about the body's right
 *  (axis 0) or forward (axis 1), and gives the arm back by `end`. Side is the take's own lazy toss (no keys). */
const FIN_START = CHUG_DUR * 0.8;
interface DitchDef { keys: number[][]; rel: number; end: number; v: [number, number, number]; spin: number; axis: 0 | 1 }
const DITCH: DitchDef[] = [
  { keys: [], rel: CHUG_DUR * CHUG_TOSS - FIN_START, end: CHUG_DUR * 0.97 - FIN_START, v: [0, 0, 0], spin: 0, axis: 1 },
  // over the shoulder, without a look back
  { keys: [[0, 0.05, 0.05, 0.3], [0.16, 0, 0.4, -0.04], [0.3, -0.06, 0.44, -0.3], [0.55, 0.14, -0.4, 0.04], [0.75, 0.12, -0.45, 0.05]],
    rel: 0.29, end: 0.75, v: [0.6, 3.4, -2.6], spin: 10, axis: 0 },
  // spiked into the dirt like a touchdown ball
  { keys: [[0, 0.05, 0.05, 0.3], [0.22, 0.12, 0.55, 0.1], [0.34, 0.2, -0.42, 0.42], [0.55, 0.2, -0.42, 0.4], [0.85, 0.12, -0.45, 0.05]],
    rel: 0.31, end: 0.85, v: [0.4, -13, 2.4], spin: 2, axis: 0 },
  // flicked end over end, watched all the way up and all the way down
  { keys: [[0, 0.05, 0.05, 0.3], [0.16, 0.12, -0.25, 0.36], [0.26, 0.08, 0.28, 0.32], [0.45, 0.12, 0.05, 0.3], [0.85, 0.12, -0.45, 0.05]],
    rel: 0.25, end: 1.1, v: [0.05, 6.2, 0.6], spin: 17, axis: 0 },
  // the mic drop: held out at arm's length, a look, and let go
  { keys: [[0, 0.05, 0.05, 0.3], [0.25, 0.6, 0.02, 0.1], [0.72, 0.6, 0.02, 0.1], [1.05, 0.12, -0.45, 0.05]],
    rel: 0.68, end: 1.15, v: [0, 0.2, 0], spin: 1.5, axis: 1 },
];
/** the fist on a ditch path at time t (eased between keys) */
function ditchKey(keys: number[][], t: number, out: THREE.Vector3) {
  let i = 0;
  while (i < keys.length - 2 && t > keys[i + 1][0]) i++;
  const a = keys[i], b = keys[i + 1], k = smooth(clamp((t - a[0]) / (b[0] - a[0]), 0, 1));
  return out.set(a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k, a[3] + (b[3] - a[3]) * k);
}
/** 0 before a, eased up to 1 at p, back down to 0 at c */
const bump = (t: number, a: number, p: number, c: number) => (t < a || t > c ? 0 : t < p ? smooth((t - a) / (p - a)) : 1 - smooth((t - p) / (c - p)));
const MAX_FLAMES = 48;
const LOD_DIST = 25;

const damp = (a: number, b: number, rate: number, dt: number) => a + (b - a) * (1 - Math.exp(-rate * dt));
const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);
const wrapPi = (a: number) => { a = (a + Math.PI) % TAU; if (a < 0) a += TAU; return a - Math.PI; };
const smooth = (t: number) => t * t * (3 - 2 * t);

// ------------------------------------------------------------------ material
/**
 * Painted-print character shading: the authored albedo kept intact, lit by a soft 4-step ramp
 * (sun + teal hemisphere), a warm sun-side rim, a cool fresnel edge, a sheen on worn metal, the
 * slot-colour tint on scarf/cape texels (mask), fire glow and the Phantom cloak shimmer.
 */
let softRamp: THREE.DataTexture | null = null;
function rampTex() {
  if (softRamp) return softRamp;
  // dark core -> soft terminator -> lit plateau; linear filtered so steps blend a little
  const vals = [84, 112, 160, 212, 238, 252, 255, 255];
  const data = new Uint8Array(vals.length * 4);
  vals.forEach((v, i) => data.set([v, v, v, 255], i * 4));
  softRamp = new THREE.DataTexture(data, vals.length, 1, THREE.RGBAFormat);
  softRamp.minFilter = THREE.LinearFilter; softRamp.magFilter = THREE.LinearFilter; softRamp.generateMipmaps = false;
  softRamp.needsUpdate = true;
  return softRamp;
}

/**
 * The scarf is the player ID at the chase camera, so it carries the slot hue (HUD tabs match), but as DYED WOOL,
 * not paint: saturation held to a natural-dye range and value kept mid, so the scene's light and the fibre shade
 * it like the rest of the kit. Rust-orange, teal, ochre and plum.
 */
const _hsl = { h: 0, s: 0, l: 0 };
export function scarfColor(slot: number): number {
  const c = new THREE.Color(SLOT_COLORS[slot & 3]);
  c.getHSL(_hsl);
  const sl = slot & 3;
  const h = sl === 0 ? _hsl.h + 0.02 : sl === 3 ? _hsl.h + 0.06 : _hsl.h;
  const sat = sl === 3 ? 0.38 : sl === 2 ? 0.55 : 0.47;
  const lit = sl === 3 ? 0.4 : sl === 2 ? 0.46 : 0.42;
  return c.setHSL(h, sat, lit).getHex();
}

export function cowboyMaterial(a: CowboyAsset, slotColor: number, cloak: boolean) {
  const m = new THREE.MeshToonMaterial({ map: a.map, gradientMap: rampTex(), transparent: cloak, opacity: cloak ? 0.4 : 1 });
  const u = {
    uMask: { value: a.mask },
    uSlot: { value: new THREE.Color(slotColor) },
    uRim: { value: new THREE.Color(PAL.sunCore).multiplyScalar(0.75) },
    uShimmer: { value: 0 },
    uCloakT: { value: 0 },
    /** playtest 1 (Nick): the pack glows red with heat and burns when it's hurting (pack fragments only: packK) */
    uPack: { value: 0 },
    uFire: { value: 0 },
    /** playtest 2 (Nick): the hyper-shield wrap (0..1, yellow over the whole body) and the shield-hit ring (world point + radius) */
    uShell: { value: 0 },
    uHit: { value: new THREE.Vector3() },
    uRing: { value: -1 },
    uFogMax: { value: 0.32 },
    uClsDesat: { value: a.name === 'juggernaut' ? 0.4 : 0 },
    uLift: { value: a.name === 'phantom' ? 0.8 : 0 },
    uHeat: { value: 0 },
  };
  m.userData.u = u;
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, HAZE, u);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vHazeWorld;\nattribute vec4 aoc;\nvarying float vAO;')
      .replace('#include <project_vertex>', '#include <project_vertex>\nvHazeWorld = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;\nvAO = aoc.w > 0.0 ? aoc.w : 1.0;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <emissivemap_fragment>', /* glsl */ `#include <emissivemap_fragment>
        {
          // the pack's own glow (the rest of the body only catches the fire's light through the material emissive)
          float packE = step( vMapUv.y, 2.0 ) * step( 0.6667, vMapUv.y ) * step( vMapUv.x, 0.5 );
          float hk2 = smoothstep( 0.5, 1.0, uPack );
          totalEmissiveRadiance += vec3( 1.0, 0.22, 0.04 ) * packE * ( hk2 * hk2 * 0.55 + uFire * 1.3 );
          // the hyper-shield wrap: the whole body lit yellow, brightest at the silhouette (a fresnel), through the i-frames
          if ( uShell > 0.0 ) {
            float fr = 1.0 - clamp( abs( dot( normalize( vNormal ), normalize( vViewPosition ) ) ), 0.0, 1.0 );
            totalEmissiveRadiance += vec3( 1.0, 0.86, 0.3 ) * uShell * ( 0.7 + 2.2 * fr * fr );
          }
          // a hit on the shield: a blue ring runs out over the body from where it came in
          if ( uRing >= 0.0 ) {
            float dHit = distance( vHazeWorld, uHit );
            float band = smoothstep( uRing - 0.3, uRing, dHit ) * ( 1.0 - smoothstep( uRing, uRing + 0.08, dHit ) );
            float fr2 = 1.0 - clamp( abs( dot( normalize( vNormal ), normalize( vViewPosition ) ) ), 0.0, 1.0 );
            totalEmissiveRadiance += vec3( 0.45, 0.8, 1.0 ) * ( band * 1.6 + 0.25 * fr2 * fr2 * ( 1.0 - smoothstep( 0.0, 3.0, uRing ) ) );
          }
        }`)
      .replace('#include <common>', /* glsl */ `#include <common>
        varying vec3 vHazeWorld; varying float vAO;
        uniform float uClsDesat; uniform float uLift; uniform float uHeat;
        float ch21( vec2 p ) { return fract( sin( dot( p, vec2( 41.3, 289.1 ) ) ) * 45758.5453 ); }
        float cvn( vec2 p ) { vec2 i = floor( p ), f = fract( p ); f = f * f * ( 3.0 - 2.0 * f );
          return mix( mix( ch21( i ), ch21( i + vec2( 1, 0 ) ), f.x ), mix( ch21( i + vec2( 0, 1 ) ), ch21( i + vec2( 1, 1 ) ), f.x ), f.y ); }
        uniform sampler2D uMask; uniform vec3 uSlot; uniform vec3 uRim; uniform float uShimmer; uniform float uCloakT; uniform float uPack; uniform float uFire;
        uniform float uShell; uniform vec3 uHit; uniform float uRing;
        uniform vec3 uHazeColor; uniform vec3 uSunHazeColor; uniform vec3 uLowHazeColor; uniform vec3 uSunDir;
        uniform float uHazeDensity; uniform float uHazeBase; uniform float uHazeHeightFalloff; uniform float uHazeLow; uniform float uFogMax;
        vec3 applyHaze( vec3 col ) {
          vec3 toFrag = vHazeWorld - cameraPosition;
          float dist = length( toFrag );
          float fd = 1.0 - exp( -pow( dist * uHazeDensity, 1.45 ) );
          float hf = exp( -max( vHazeWorld.y - uHazeBase, 0.0 ) * uHazeHeightFalloff );
          float f = fd * ( 0.6 + 0.4 * hf ) + hf * uHazeLow * smoothstep( 25.0, 160.0, dist ) * 0.55;
          f = clamp( f, 0.0, uFogMax );
          float sunAmt = pow( max( dot( toFrag / max( dist, 1e-3 ), uSunDir ), 0.0 ), 5.0 );
          vec3 hz = mix( mix( uHazeColor, uLowHazeColor, hf * 0.6 ), uSunHazeColor, sunAmt * 0.8 );
          return mix( col, hz, f );
        }`)
      .replace('#include <map_fragment>', /* glsl */ `#include <map_fragment>
        float metalK = 0.0; float warmK = 0.0;
        float packK = step( vMapUv.y, 2.0 ) * step( 0.6667, vMapUv.y ) * step( vMapUv.x, 0.5 );
        {
          // local-contrast lift: the texture minus a 3-mip blur is its panel seams, rivets, decals and chips;
          // amplified so they survive the gameplay distance (strongest on the pack, the biggest area on screen)
          // the pack (the biggest area at the chase camera) samples a sharper mip
          if ( packK > 0.5 ) diffuseColor.rgb = texture2D( map, vMapUv, -0.8 ).rgb;
          vec3 blur = texture2D( map, vMapUv, 3.0 ).rgb;
          vec3 hp = diffuseColor.rgb - blur;
          diffuseColor.rgb = max( blur + hp * mix( 1.45, 2.6, packK ), 0.0 );
          // the pack: dirty enamel, not white plastic: capped highs, warm off-white, soot and grime in streaks
          if ( packK > 0.5 ) {
            float pl = dot( diffuseColor.rgb, vec3( 0.299, 0.587, 0.114 ) );
            float grime = cvn( vMapUv * vec2( 90.0, 40.0 ) ) * 0.6 + cvn( vMapUv * 300.0 ) * 0.4;
            diffuseColor.rgb = diffuseColor.rgb * vec3( 0.95, 0.92, 0.85 ) * ( 1.0 - 0.2 * smoothstep( 0.45, 0.85, grime ) );
            diffuseColor.rgb = min( diffuseColor.rgb, vec3( 0.74, 0.71, 0.63 ) + 0.06 * pl );
            // heat: the enamel goes dull red from the middle of the gauge, cherry near the top, then it burns
            float hk = smoothstep( 0.35, 1.0, uPack );
            diffuseColor.rgb = mix( diffuseColor.rgb, vec3( 0.62, 0.07, 0.03 ), hk * 0.85 );
          }
          // tan plates -> the ref's worn silver (brass is more saturated and is kept; leather is darker and is kept)
          {
            float x0 = max( diffuseColor.r, max( diffuseColor.g, diffuseColor.b ) ), n0 = min( diffuseColor.r, min( diffuseColor.g, diffuseColor.b ) );
            float sat0 = ( x0 - n0 ) / max( x0, 1e-3 );
            float tanK = smoothstep( 0.08, 0.16, sat0 ) * ( 1.0 - smoothstep( 0.36, 0.48, sat0 ) ) * smoothstep( 0.1, 0.22, x0 ) * step( diffuseColor.b, diffuseColor.r ) * ( 1.0 - packK );
            float l0 = dot( diffuseColor.rgb, vec3( 0.299, 0.587, 0.114 ) );
            diffuseColor.rgb = mix( diffuseColor.rgb, vec3( l0 ) * vec3( 0.97, 1.0, 1.06 ) * 1.08, tanK * 0.7 );
            diffuseColor.rgb = mix( diffuseColor.rgb, vec3( l0 ), uClsDesat );
          }
        }
        {
          // slot tint on scarf / cape texels, keeping the cloth's own value detail
          float mk = texture2D( uMask, vMapUv ).r;
          float lum = dot( diffuseColor.rgb, vec3( 0.299, 0.587, 0.114 ) );
          // dyed wool, not paint: the slot hue is slightly dusted toward grey and carries the
          // texture's folds and wear at full contrast (lum^1.2), so the scarf keeps its weave
          // the cowl is the player ID: vivid dye with the texture's folds as a gentle modulation (never to black)
          vec3 tint = uSlot * clamp( 0.55 + ( lum - 0.25 ) * 1.6, 0.4, 1.15 );
          // the cowl's own wool swatch: felted fibre noise at ~1 cm, lit by the scene like any other cloth
          float woolK = step( 0.958, vMapUv.y ) * step( 0.9375, vMapUv.x );
          {
            vec2 fc = vMapUv * vec2( 2048.0, 3072.0 );
            float fib = cvn( fc * vec2( 0.9, 0.4 ) ) * 0.55 + cvn( fc * vec2( 2.6, 1.1 ) ) * 0.45;
            vec3 wool = uSlot * ( 0.15 + lum * 1.25 ) * ( 0.82 + 0.26 * fib );
            tint = mix( tint, wool, woolK );
          }
          // the dye sits ~70 % over the wool: grey fibre and fold values show through, so it reads as dyed cloth
          diffuseColor.rgb = mix( diffuseColor.rgb, tint, smoothstep( 0.25, 0.75, mk ) * mix( 0.95, 0.7, woolK ) );
          // hat: grey-brown felt (mask G), keeping the texture's crease and wear values
          float hk = texture2D( uMask, vMapUv ).g * ( 1.0 - smoothstep( 0.25, 0.75, mk ) );
          diffuseColor.rgb = mix( diffuseColor.rgb, vec3( 0.2, 0.16, 0.12 ) * clamp( 0.45 + lum * 1.3, 0.4, 1.4 ), hk * 0.9 );
          // per-class mid-value lift (Phantom's black-on-black plating)
          diffuseColor.rgb = mix( diffuseColor.rgb, pow( diffuseColor.rgb, vec3( 0.72 ) ) * 1.1, uLift );
          // worn metal: bright, desaturated texels get a sheen below
          float mx = max( diffuseColor.r, max( diffuseColor.g, diffuseColor.b ) );
          float mn = min( diffuseColor.r, min( diffuseColor.g, diffuseColor.b ) );
          metalK = smoothstep( 0.18, 0.45, mx ) * ( 1.0 - smoothstep( 0.08, 0.3, ( mx - mn ) / max( mx, 1e-3 ) ) ) * ( 1.0 - mk );
          // a touch of print contrast on the albedo
          diffuseColor.rgb = pow( diffuseColor.rgb, vec3( 1.08 ) ) * 1.06;
          // keep the ref's temperature split under the golden key: the sun would turn blue steel and denim khaki,
          // so the albedo's own chroma is pushed (x1.35) and bare steel leans cool. Brass and the scarf stay warm.
          float dl0 = dot( diffuseColor.rgb, vec3( 0.299, 0.587, 0.114 ) );
          diffuseColor.rgb = max( mix( vec3( dl0 ), diffuseColor.rgb, 1.35 ), 0.0 );
          float mx0 = max( diffuseColor.r, max( diffuseColor.g, diffuseColor.b ) );
          warmK = max( smoothstep( 0.12, 0.35, ( diffuseColor.r - diffuseColor.b ) / max( mx0, 1e-3 ) ), smoothstep( 0.25, 0.75, mk ) );
          // baked AO: crevices and the shade under the brim, pack and belt (squared for a printed falloff)
          float aoK = clamp( vAO * 1.35, 0.0, 1.0 ); // open surfaces bake ~0.75 (Tripo shells overlap); remap them to 1
          diffuseColor.rgb *= mix( 0.42, 1.0, aoK * aoK );
        }`)
      .replace('#include <opaque_fragment>', /* glsl */ `
        {
          vec3 vdir = normalize( vViewPosition );
          vec3 nn = normalize( normal );
          float fres = 1.0 - clamp( dot( nn, vdir ), 0.0, 1.0 );
          vec3 sunV = normalize( ( viewMatrix * vec4( uSunDir, 0.0 ) ).xyz );
          float sunSide = smoothstep( -0.1, 0.4, dot( nn, sunV ) );
          // warm sun rim (hard, printed) + cool sky edge on the shadow side
          float rim = smoothstep( 0.55, 0.72, fres );
          outgoingLight += uRim * rim * sunSide * 0.6;
          outgoingLight += vec3( 0.16, 0.26, 0.24 ) * rim * ( 1.0 - sunSide ) * 0.5;
          // shadow-side fill: warm ground bounce from below + soft sky from above, so the
          // unlit half keeps its form instead of going to mud (the ref's soft studio fill)
          float upN = ( transpose( mat3( viewMatrix ) ) * nn ).y;
          vec3 fill = mix( vec3( 0.42, 0.26, 0.12 ), vec3( 0.30, 0.30, 0.32 ), smoothstep( -0.6, 0.6, upN ) );
          outgoingLight += diffuseColor.rgb * fill * ( 1.0 - sunSide ) * 0.55;
          // metal sheen: a broad Blinn lobe toward the sun, banded
          vec3 hv = normalize( sunV + vdir );
          float spec = pow( max( dot( nn, hv ), 0.0 ), 22.0 );
          outgoingLight += uRim * smoothstep( 0.25, 0.55, spec ) * metalK * 0.55;
          // cool everything that isn't brass, leather or the scarf: the golden key would otherwise turn the ref's
          // blue steel and denim khaki and lose its warm-on-cool split
          outgoingLight = mix( outgoingLight, outgoingLight * vec3( 0.84, 0.96, 1.16 ), ( 1.0 - warmK ) * 0.75 );
          // glossy enamel on the pack: a tight hot highlight + a soft sky sheen at grazing angles
          float gl = pow( max( dot( nn, hv ), 0.0 ), 70.0 );
          outgoingLight += ( uRim * 1.3 * smoothstep( 0.3, 0.7, gl ) + vec3( 0.16, 0.18, 0.2 ) * smoothstep( 0.5, 0.95, fres ) ) * packK;
          // jetpack.png's studio gloss, view-anchored so it reads from the chase camera whatever the sun does: a
          // hot streak along the pack's upper curves (light from above and behind the camera)
          vec3 hv2 = normalize( vdir + mat3( viewMatrix ) * vec3( 0.0, 0.9, 0.0 ) );
          float gl2 = pow( max( dot( nn, hv2 ), 0.0 ), 48.0 );
          outgoingLight += vec3( 0.62, 0.6, 0.56 ) * smoothstep( 0.3, 0.8, gl2 ) * packK;
          // plate edges catch the light (the ref's bright chipped rims) and upward plates take the sky
          outgoingLight += uRim * smoothstep( 0.4, 0.62, fres ) * sunSide * metalK * 0.3;
          outgoingLight += diffuseColor.rgb * vec3( 0.32, 0.34, 0.38 ) * smoothstep( 0.2, 0.9, upN ) * ( 0.35 + 0.65 * metalK ) * 0.4;
          // jet nozzles (atlas swatch 6): the inner bell and the petal lip glow with the burn, dull cherry at hover
          // to straw-white at full burner; the outer bell takes a faint heat flush near the lip
          {
            float nozK = step( 0.875, vMapUv.x ) * step( vMapUv.x, 0.9375 ) * step( 0.9583, vMapUv.y );
            float sN = clamp( ( vMapUv.y * 3072.0 - 2958.0 ) / 100.0, 0.0, 1.0 );
            float inner = smoothstep( 0.55, 0.68, sN ) * ( 0.45 + 0.55 * smoothstep( 0.7, 1.0, sN ) );
            float lip = exp( -pow( ( sN - 0.6 ) / 0.05, 2.0 ) );
            float flush = smoothstep( 0.4, 0.56, sN ) * ( 1.0 - smoothstep( 0.56, 0.6, sN ) );
            vec3 hc = mix( vec3( 0.55, 0.06, 0.015 ), vec3( 2.6, 1.25, 0.45 ), smoothstep( 0.35, 1.0, uHeat ) );
            outgoingLight += hc * ( inner * 0.9 + lip * 1.1 + flush * 0.35 * uHeat ) * smoothstep( 0.05, 0.45, uHeat ) * nozK;
          }
          if ( uShimmer > 0.0 ) {
            // the Phantom's cloak (CLASSES.md): the body goes to smoked glass with a violet-to-cyan prism rim,
            // fine scan lines crawl up it, a cell pattern breaks it up, and a bright seam sweeps up the body
            // as the cloak engages (uShimmer ramps 0 to 1 in about a third of a second)
            vec3 wp = vHazeWorld;
            float band = 0.5 + 0.5 * sin( wp.y * 38.0 - uCloakT * 9.0 + wp.x * 6.0 );
            float scan = smoothstep( 0.82, 1.0, 0.5 + 0.5 * sin( wp.y * 90.0 - uCloakT * 14.0 ) );
            vec3 cq = floor( wp * 9.0 + vec3( 0.0, uCloakT * 1.5, 0.0 ) );
            float cell = fract( sin( dot( cq, vec3( 12.99, 78.23, 37.71 ) ) ) * 43758.55 );
            float hole = step( 0.78, cell ) * ( 0.6 + 0.4 * sin( uCloakT * 6.0 + cell * 40.0 ) );
            vec3 prism = mix( vec3( 0.62, 0.42, 1.0 ), vec3( 0.55, 0.95, 1.0 ), 0.5 + 0.5 * sin( fres * 6.0 + uCloakT * 2.5 + wp.y * 3.0 ) );
            float ramp = clamp( uShimmer, 0.0, 1.0 );
            float sweepY = fract( wp.y * 0.5 ) - ramp; // a seam that runs up the body while the cloak engages
            float sweep = exp( -pow( sweepY / 0.12, 2.0 ) ) * ( 1.0 - ramp ) * 4.0;
            vec3 glass = outgoingLight * 0.15 + prism * 0.08;
            outgoingLight = mix( outgoingLight, glass, ramp );
            outgoingLight += prism * ramp * ( pow( fres, 1.6 ) * 0.9 + scan * 0.3 * band + hole * 0.2 ) + prism * sweep;
            diffuseColor.a *= 1.0 - 0.45 * ramp;
            diffuseColor.a = mix( diffuseColor.a, 0.5, ramp * ( pow( fres, 1.4 ) * 0.9 + scan * 0.3 ) );
            diffuseColor.a = max( diffuseColor.a * ( 1.0 - 0.7 * hole * ramp ), sweep * 0.6 );
          }
        }
        #include <opaque_fragment>`)
      .replace('#include <fog_fragment>', 'gl_FragColor.rgb = applyHaze( gl_FragColor.rgb );');
  };
  m.customProgramCacheKey = () => `cowboyTex_${cloak}`;
  return m;
}

/**
 * Fighter-jet thruster plume, one instanced additive draw for every nozzle of every cowboy.
 * Instance matrix: origin at the nozzle exit, local -Y along the jet, scale (exit radius, length, exit radius).
 * instanceColor: r = throttle 0..1 (smoothed, from the sim's real thrust), g = visibility (cloak), b = seed.
 * Low throttle (hover): a short, translucent blue-violet cone with faint diamonds. High throttle (flight / boost):
 * a long plume, white-hot HDR core (> 1.25 linear, so post.ts blooms it), a chain of shock diamonds with Mach
 * disks, and an orange afterburner envelope that flickers and fades to nothing; the tail wobbles (heat shimmer).
 * The cylinder is shaded by view angle (|n.v| ~ distance from the jet axis), so it never shows a hard edge.
 */
const plumeT = { value: 0 };
function plumeMaterial() {
  return new THREE.ShaderMaterial({
    uniforms: { uT: plumeT },
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    vertexShader: /* glsl */ `
      uniform float uT;
      varying float vT; varying float vF; varying float vThr; varying float vVis; varying float vSeed; varying float vAng;
      void main() {
        vec3 ic = vec3( 1.0 );
        #ifdef USE_INSTANCING_COLOR
          ic = instanceColor;
        #endif
        float thr = ic.r;
        float t = clamp( -position.y, 0.0, 1.0 );
        // radius profile in exit radii: hover = a tapering cone; full = the afterburner barrel that swells and
        // stays wide (the envelope), its end dissolving in the fragment shader
        float rLo = 0.92 * ( 1.0 - 0.7 * t );
        float rHi = 1.0 + 1.7 * t - 1.05 * t * t;
        float rad = mix( rLo, rHi, smoothstep( 0.0, 1.0, thr ) );
        float ang = atan( position.z, position.x );
        // heat shimmer: the far half of the jet wobbles
        float wob = sin( uT * 31.0 + t * 11.0 + ang * 2.0 + ic.b * 40.0 ) * 0.07 + sin( uT * 53.0 - t * 17.0 ) * 0.04;
        vec3 p = vec3( position.x * rad * ( 1.0 + wob * t ), position.y, position.z * rad * ( 1.0 + wob * t ) );
        mat4 im = instanceMatrix;
        vec4 wp = modelMatrix * im * vec4( p, 1.0 );
        vec3 n = normalize( mat3( modelMatrix ) * mat3( im ) * vec3( normal.x, 0.0, normal.z ) );
        vec3 v = normalize( cameraPosition - wp.xyz );
        vF = abs( dot( n, v ) );
        vT = t; vThr = thr; vVis = ic.g; vSeed = ic.b * 17.0; vAng = ang;
        gl_Position = projectionMatrix * viewMatrix * wp;
      }`,
    fragmentShader: /* glsl */ `
      uniform float uT;
      varying float vT; varying float vF; varying float vThr; varying float vVis; varying float vSeed; varying float vAng;
      float h( vec2 p ) { return fract( sin( dot( p, vec2( 12.9898, 78.233 ) ) ) * 43758.5453 ); }
      float n2( vec2 p ) { vec2 i = floor( p ), f = fract( p ); f = f * f * ( 3.0 - 2.0 * f );
        return mix( mix( h( i ), h( i + vec2( 1, 0 ) ), f.x ), mix( h( i + vec2( 0, 1 ) ), h( i + vec2( 1, 1 ) ), f.x ), f.y ); }
      void main() {
        float thr = vThr, t = vT;
        float hi = smoothstep( 0.35, 0.95, thr );
        // ~ distance from the jet axis: 0 on the axis, 1 at the silhouette
        float rho = sqrt( max( 0.0, 1.0 - vF * vF ) );
        float tongue = n2( vec2( vAng * 1.3 + vSeed, t * 6.0 - uT * 26.0 + vSeed ) );
        float tongue2 = n2( vec2( vAng * 2.7 - vSeed, t * 13.0 - uT * 41.0 ) );
        // ---- white-hot core: tight, brightest right at the exit, a fraction of the jet long
        float cl = mix( 0.42, 0.62, hi );
        float coreW = mix( 0.45, 0.52, hi ) * ( 1.0 - 0.7 * t / cl );
        float core = ( 1.0 - smoothstep( coreW * 0.55, coreW, rho ) ) * ( 1.0 - smoothstep( cl * 0.55, cl, t ) );
        // ---- shock diamonds: a chain of rhombi along the core, each pinched at both ends, with a bright Mach disk
        float dl = mix( 0.62, 0.8, hi );
        float N = mix( 3.0, 5.0, hi );
        float ph = clamp( ( t - 0.03 ) / dl, 0.0, 1.0 ) * N;
        float f = fract( ph );
        float tri = 1.0 - abs( f * 2.0 - 1.0 );
        float w = ( 0.08 + 0.42 * tri ) * ( 1.0 - 0.45 * t / dl );
        float diam = ( 1.0 - smoothstep( w * 0.6, w, rho ) ) * step( t, dl + 0.03 ) * step( 0.03, t );
        float disk = exp( -pow( ( f - 0.5 ) * 9.0, 2.0 ) ) * ( 1.0 - smoothstep( 0.18, 0.34, rho ) ) * step( t, dl );
        float dFade = 1.0 - smoothstep( dl * 0.5, dl, t );
        // ---- envelope: violet near the nozzle, orange afterburner flame, then dull red, gone at the tail
        float endT = mix( 0.55, 0.7, hi ) + 0.3 * tongue;
        float env = pow( vF, 1.3 ) * ( 1.0 - smoothstep( endT * 0.45, endT + 0.2, t ) ) * ( 0.55 + 0.6 * tongue * tongue2 + 0.25 * tongue );
        vec3 violet = vec3( 0.42, 0.36, 1.25 );
        vec3 flame = mix( vec3( 0.7, 0.45, 1.2 ), vec3( 2.2, 0.85, 0.22 ), smoothstep( 0.0, 0.16, t ) );
        flame = mix( flame, vec3( 0.75, 0.14, 0.04 ), smoothstep( 0.45, 0.95, t ) );
        vec3 envCol = mix( violet, flame, hi );
        vec3 coreCol = mix( vec3( 1.1, 1.15, 2.3 ), vec3( 6.0, 5.8, 5.4 ), hi );
        vec3 diamCol = mix( vec3( 0.62, 0.55, 1.7 ), vec3( 3.0, 2.15, 1.25 ), hi );
        // exit glow: the whole mouth burns at the nozzle plane
        float mouth = 1.0 - smoothstep( 0.0, 0.06, t );
        float flick = 0.88 + 0.12 * sin( uT * 67.0 + vSeed * 3.0 ) + 0.1 * ( tongue - 0.5 );
        vec3 col = envCol * env * mix( 0.5, 0.85, hi )
          + coreCol * core * mix( 0.8, 1.15, hi ) * flick
          + diamCol * ( diam * mix( 0.55, 1.15, hi ) + disk * mix( 0.9, 1.8, hi ) ) * dFade * flick
          + coreCol * mouth * 0.35 * ( 1.0 - rho * 0.5 );
        col *= vVis * smoothstep( 0.02, 0.12, thr );
        gl_FragColor = vec4( col, 1.0 );
      }`,
  });
}

function flameGeometry() {
  // unit open cylinder (radius 1, y 0 .. -1): the vertex shader shapes it per throttle; dense rings so the
  // diamonds and the envelope taper stay smooth
  const pts: THREE.Vector2[] = [];
  for (let i = 0; i <= 14; i++) { const t = Math.pow(i / 14, 1.25); pts.push(new THREE.Vector2(1, -t)); }
  const g = new THREE.LatheGeometry(pts, 14);
  g.deleteAttribute('uv');
  return g;
}

// ------------------------------------------------------------------ per-asset rig data
interface Rig {
  a: CowboyAsset;
  upper: Float32Array; // 0 legs/hips .. 1 arms/head
  lower: Float32Array;
  arms: Float32Array;
  /** everything but the arms (the blade's IK owns them): melee body clips */
  noArms: Float32Array;
  full: Float32Array;
  clip: Record<string, ClipData>;
  b: Record<string, number>;
  nozzle: THREE.Matrix4[];
  nozR: Float32Array;
  holster: { bone: number; pos: THREE.Vector3; q: THREE.Quaternion };
  /** stow sockets for any weapon: [StowK.Thigh] right-thigh holster, [StowK.Back] slung on the pack */
  stows: { bone: number; pos: THREE.Vector3; q: THREE.Quaternion }[];
  /** the lips, head-local (where the whiskey bottle's neck goes) */
  mouth: THREE.Vector3;
  spheres: { bone: number; pos: THREE.Vector3; r: number }[];
  ribbons: RibbonDef[];
  /** torso and hips yaw of the aim pose relative to model forward (radians) */
  twist: { torso: number; hips: number };
  /** arms hanging relaxed (gun lowered), local quats; only arm bones are used */
  relaxed: Float32Array;
  /** bind-pose locals: the defined base under procedural overrides (hero landing) */
  bind: Float32Array;
  fingers: Uint8Array;
}
const rigCache = new Map<CowboyAsset, Rig>();
const NEG_Y = new THREE.Vector3(0, -1, 0);

function buildRig(a: CowboyAsset, cls: number): Rig {
  let r = rigCache.get(a);
  if (r) return r;
  const nB = a.nB;
  const upper = new Float32Array(nB), arms = new Float32Array(nB), full = new Float32Array(nB).fill(1);
  for (let i = 0; i < nB; i++) {
    const n = a.bones[i].n;
    const isArm = /Shoulder|Arm|Hand/.test(n);
    arms[i] = isArm ? 1 : 0;
    upper[i] = isArm || /Neck|Head|Spine2/.test(n) ? 1 : n === 'Spine1' ? 0.7 : n === 'Spine' ? 0.35 : 0;
  }
  const lower = upper.map((u) => 1 - u) as Float32Array;
  const noArms = arms.map((v) => 1 - v) as Float32Array;
  const b: Record<string, number> = {};
  for (const k of ['Hips', 'Spine', 'Spine1', 'Spine2', 'Neck', 'Head', 'LeftShoulder', 'RightShoulder', 'LeftArm', 'RightArm', 'LeftForeArm', 'RightForeArm', 'LeftHand', 'RightHand', 'LeftUpLeg', 'RightUpLeg', 'LeftLeg', 'RightLeg', 'LeftFoot', 'RightFoot']) b[k] = a.idx[k] ?? -1;
  const clip: Record<string, ClipData> = {};
  for (const [k, v] of a.clips) clip[k] = v;

  const sp2 = b.Spine2;
  const PK = nB + 1; // the pack bone (same bind as Spine2; see CowboyView.build)
  const toLocal = (bone: number, p: THREE.Vector3) => p.clone().sub(a.bindW[bone]).applyQuaternion(a.bindWq[bone].clone().invert());
  const qLocal = (bone: number, q: THREE.Quaternion) => a.bindWq[bone].clone().invert().multiply(q);
  const nozzle = a.nozzles.map((n) => new THREE.Matrix4().compose(n.pos, new THREE.Quaternion().setFromUnitVectors(NEG_Y, n.dir.clone().normalize()), new THREE.Vector3(1, 1, 1)));

  // stow sockets: pistols ride the right thigh, long guns hang along the right side of the pack. Every class gets
  // both, because the weapon in hand (not the class) decides where it goes away
  const H = a.height;
  let thigh, back;
  {
    const bone = b.RightUpLeg;
    const p = new THREE.Vector3(-0.2 * (H / 1.9), a.bindW[bone].y - 0.12, 0.02);
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI / 2 + 0.15, 0, 0)); // barrel down
    thigh = { bone, pos: toLocal(bone, p), q: qLocal(bone, q) };
  }
  {
    const px = Math.max(Math.abs(a.pack.min.x), Math.abs(a.pack.max.x));
    const p = new THREE.Vector3(-(px + 0.08), (a.pack.min.y + a.pack.max.y) * 0.5 + 0.05, (a.pack.min.z + a.pack.max.z) * 0.5);
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI / 2 - 0.35, 0, 0.45)); // barrel down, slung clear of the pack
    back = { bone: PK, pos: toLocal(sp2, p), q: qLocal(sp2, q) };
  }
  const holster = a.pistol ? thigh : back;
  // the mouth: a little up and forward of the head joint (the asset faces +Z)
  const mouth = toLocal(b.Head, a.bindW[b.Head].clone().add(new THREE.Vector3(0, -0.02, 0.12).multiplyScalar(H / 1.9)));
  const stows = [thigh, back];
  // cloth colliders: chest, pack, hips (model space -> bone local)
  const pc = new THREE.Vector3().addVectors(a.pack.min, a.pack.max).multiplyScalar(0.5);
  const spheres = [
    { bone: sp2, pos: toLocal(sp2, new THREE.Vector3(0, a.bindW[sp2].y - 0.02, 0)), r: 0.2 * (H / 1.9) },
    { bone: b.Hips, pos: toLocal(b.Hips, new THREE.Vector3(0, a.bindW[b.Hips].y, -0.02)), r: 0.21 * (H / 1.9) },
  ];
  // scarf tails: the cowl is knotted at the back of the neck and its two tails are cloth. The pack sits below the
  // collar (asset.ts seats it there), so the tails lie back over the pack's top and fall down its outer face: the
  // slot colour is the first thing the chase camera sees. The pack is a box collider (cloth.ts boxOut).
  const nk = a.bindW[b.Neck];
  const ny = Math.max(a.pack.max.y + 0.06, nk.y - 0.015);
  const nz = nk.z - 0.1;
  const col = 0xffffff;
  // each tail: three columns, the middle one set out 1.5 cm (a pleat the cloth keeps, lit as a real fold), narrowing
  // to a ragged alpha-cut fringe (cloth shader). Unequal lengths and splayed, like a knotted bandana.
  const ribbons: RibbonDef[] = [];
  const big = cls === 3 ? 1.15 : 1;
  const seg = 0.055 * big;
  const over = Math.max(2, Math.ceil((nz - a.pack.min.z + 0.02) / seg)); // links to clear the pack's top
  for (const side of [-1, 1]) {
    const w = 0.075 * big, x0 = side * 0.04;
    ribbons.push({
      bone: sp2, anchors: [-0.5, 0, 0.5].map((f, i) => [x0 + side * w * f, ny + (i === 1 ? 0.004 : 0), nz - (i === 1 ? 0.015 : 0)] as V3),
      rows: over + (side > 0 ? 6 : 5), seg, hem: [0.04, 0.0, 0.05], color: col, windK: cls === 3 ? 0.6 : 0.9,
      hang: [side * 0.06, -1, -0.1], taper: 0.4, bend: { n: over, dir: [0, -0.1, -1] }, restBone: PK,
    });
  }

  // how far the aim clip turns the chest away from the aim line (Mixamo rifle stances blade ~90 deg)
  const twist = { torso: 0, hips: 0 };
  {
    const c = a.pistol ? clip.idle_p : clip.idle_r;
    const q = new THREE.Quaternion(), lq = new THREE.Quaternion();
    const yawOf = (bone: number, wq: THREE.Quaternion) => {
      const f = new THREE.Vector3(0, 0, 1).applyQuaternion(a.bindWq[bone].clone().invert()).applyQuaternion(wq);
      return Math.atan2(f.x, f.z);
    };
    for (const n of ['Hips', 'Spine', 'Spine1', 'Spine2']) {
      const i = b[n];
      if (i < 0 || !c) continue;
      lq.set(c.q[i * 4] / 32767, c.q[i * 4 + 1] / 32767, c.q[i * 4 + 2] / 32767, c.q[i * 4 + 3] / 32767).normalize();
      q.multiply(lq);
      if (n === 'Hips') twist.hips = yawOf(i, q);
      if (n === 'Spine2') twist.torso = yawOf(i, q);
    }
  }
  // relaxed arms: T-pose arms swung down to the sides, elbows soft
  const relaxed = new Float32Array(a.tpose);
  const bind = Float32Array.from(a.bones.flatMap((d) => d.q));
  {
    const q = new THREE.Quaternion(), d = new THREE.Quaternion(), t = new THREE.Quaternion();
    const post = (i: number, axis: THREE.Vector3, ang: number) => {
      if (i < 0) return;
      q.fromArray(relaxed, i * 4);
      d.setFromAxisAngle(axis, ang);
      t.copy(a.aq[i]).invert().multiply(d).multiply(a.aq[i]);
      q.multiply(t).toArray(relaxed, i * 4);
    };
    const Z = new THREE.Vector3(0, 0, 1), X = new THREE.Vector3(1, 0, 0);
    post(b.LeftArm, Z, -1.22); post(b.RightArm, Z, 1.18);
    post(b.LeftArm, X, -0.08); post(b.RightArm, X, -0.12);
    post(b.LeftForeArm, X, -0.35); post(b.RightForeArm, X, -0.5);
  }
  r = { a, upper, lower, arms, noArms, full, clip, b, nozzle, nozR: Float32Array.from(a.nozzles.map((n) => n.r)), holster, stows, mouth, spheres, ribbons, twist, relaxed, bind, fingers: Uint8Array.from(a.bones.map((d) => (/Hand.+\d/.test(d.n) ? 1 : 0))) };
  rigCache.set(a, r);
  return r;
}

// scratch
const _m = new THREE.Matrix4();
const _m2 = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _q3 = new THREE.Quaternion();
const _qa = new THREE.Quaternion();
const _v = new THREE.Vector3();
const _bP = new THREE.Vector3(), _bM = new THREE.Vector3(), _bD = new THREE.Vector3(), _bU = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _s = new THREE.Vector3();
const _e = new THREE.Euler();
const _col = new THREE.Color();
const X_AXIS = new THREE.Vector3(1, 0, 0);
const Y_AXIS = new THREE.Vector3(0, 1, 0);
const Z_AXIS = new THREE.Vector3(0, 0, 1);
const ONE = new THREE.Vector3(1, 1, 1);
/** Weapons lane (client/weapons/guns.ts GunRack.place): draws the held weapon on the gun bone and
 *  returns true to hide the baked class gun. Set through CowboyView.setGunHook. */
type GunHook = (id: number, weapon: number, gunWorld: THREE.Matrix4, leftHand: THREE.Matrix4 | null, muzzleOut: THREE.Vector3, visible: boolean, act?: GunHookAct) => boolean;
/** what the gun is doing, for its moving parts: reload weight and cycle phase / index, shot age, aim */
export interface GunHookAct { reload: number; u: number; n: number; shotAge: number; ads: number }
const _act: GunHookAct = { reload: 0, u: 0, n: 0, shotAge: 99, ads: 0 };
let gunHook: GunHook | null = null;
const _gw = new THREE.Matrix4();
const _pose: Pose = { x: 0, y: 0, z: 0, pitch: 0, yaw: 0, roll: 0 };
const _lhm = new THREE.Matrix4();
const UP_Y = new THREE.Vector3(0, 1, 0);

// per-class tuning
const CLS = [
  { melee: 'melee_p', mWin: [0.3, 0.62], mFull: false, wing: { out: 1.15, sweep: 0.35, sway: 1 }, recoil: { amp: 0.35, decay: 9 } },
  { melee: 'melee_r', mWin: [0.25, 0.55], mFull: false, wing: { out: 1.25, sweep: 0.45, sway: 1.4 }, recoil: { amp: 0.1, decay: 22 } },
  { melee: 'melee_k', mWin: [0.3, 0.6], mFull: true, wing: { out: 1.05, sweep: 0.3, sway: 0.8 }, recoil: { amp: 0.2, decay: 14 } },
  { melee: 'melee_b', mWin: [0.2, 0.6], mFull: true, wing: { out: 1.35, sweep: 0.2, sway: 0.5 }, recoil: { amp: 0.08, decay: 25 } },
];

// ------------------------------------------------------------------ one cowboy
class Cowboy {
  id = -1;
  cls = -1;
  slot = -1;
  seen = false;
  loading = false;
  rig: Rig | null = null;
  readonly group = new THREE.Object3D();
  readonly tumble = new THREE.Object3D();
  readonly model = new THREE.Object3D();
  body: THREE.SkinnedMesh | null = null;
  outline: THREE.SkinnedMesh | null = null;
  bones: THREE.Bone[] = [];
  gun!: THREE.Bone;
  /** melee lane: the class energy blade (client/render/cowboys/blades.ts); its meshes live in bladeRoot (world space) */
  blade: Blade | null = null;
  readonly bladeRoot = new THREE.Group();
  /** heal lane: the bottle in the hand (lives in bladeRoot, world space) and the view's thrown empties */
  bottle: Bottle | null = null;
  tosses: Tosses | null = null;
  private tossed = false;
  /** the ditch this chug ends in (locked once the finish starts) */
  private ditchNow = 0;
  private lastDa = 99;
  ribbons: Ribbon[] = [];
  readonly spheres = new Float32Array(4 * 8);
  /** the jet pack as a cloth box collider (cloth.ts boxOut layout) */
  readonly packBox = new Float32Array(38);
  readonly muzzleWorld = new THREE.Vector3();
  hasMuzzle = false;
  matSolid: THREE.MeshToonMaterial | null = null;
  matCloak: THREE.MeshToonMaterial | null = null;
  olSolid = outlineMaterial(0.012);
  olCloak = (() => { const m = outlineMaterial(0.016, 0xa98aff); m.transparent = true; m.opacity = 0.6; return m; })();
  // pose buffers
  acc = new Float32Array(0);
  wsum = new Float32Array(0);
  modeMask = new Float32Array(0);
  aimMask = new Float32Array(0);
  readonly hip = new Float32Array(4);
  // animation state
  fresh = true;
  bodyYaw = 0;
  turning = false;
  turnT = 0;
  yawRate = 0;
  bank = 0;
  bankRate = 0;
  armLag = 0;
  runPhase = 0;
  strideK = 1;
  /** melee lane: the weight and hips of the flourish's mocap body this frame (0 when none) */
  bodyW = 0;
  readonly bodyHip = new THREE.Vector3();
  /** planted feet (feet.ts): the blended gait of the last frame's locomotion layers, and the foot lock */
  readonly feet = new FootLock();
  private gTx = 0; private gTz = 0; private gW = 0; private gStride = 0;
  private cL = 0; private cR = 0; private cW = 0;
  private stepL = 0; private stepR = 0; private strideHz = 1;
  runAmt = 0;
  dirAng = 0;
  w = new Float32Array(6); // ground air hover flight down sky
  dead = 0;
  deadT = 0;
  downT = 0;
  holster = 0;
  /** 0..1 how far the gun is put away (the melee lane reads this to time its blade) */
  gunStowed = 0;
  /** the weapon currently in the hands (lags c.weapon through a swap) and how it's held */
  shown = -2;
  hold: Hold = holdOf(-1, false);
  swapDraw = 0.35;
  /** the hips' smoothed standing yaw off the facing (rad), taken out so the cowboy stands square */
  hipYaw = 0;
  /** the long-gun carry's chest turn this frame (rad), which the pack doesn't follow */
  carryTwist = 0;
  chestYaw = 0;
  drawing = false;
  /** the held gun's muzzle length (m), measured from the placed weapon model each frame */
  muzLen = 0.55;
  /** a body-frame gun pose (holds.ts: x right, y up, z forward, scaled) -> world position + rotation */
  private bodyPose(p: Pose, sc: number, pos: THREE.Vector3, q: THREE.Quaternion) {
    pos.set(p.x * sc, p.y * sc, -p.z * sc);
    this.group.localToWorld(pos);
    // the gun's +Z barrel: Ry(PI - yaw) faces it forward (yaw > 0 swings the muzzle across to the left)
    q.setFromEuler(_e.set(-p.pitch, Math.PI - p.yaw, p.roll, 'YXZ'));
    this.group.getWorldQuaternion(_qa);
    q.premultiply(_qa);
  }
  private drawOf(w: number): number { return w >= 0 && w <= 200 ? weaponDef(w).draw : 0.35; }
  ready = 0;
  reload = 0;
  /** idle fidgets: how long he has stood still and relaxed, the playing fidget's clock (-1 none), how many played */
  stillT = 0; fidgetAge = -1; fidgetN = 0;
  reloadT = 0;
  flightPitch = -Math.PI / 2;
  lastMove = 0;
  baseMode = 0;
  dodgeAge = 99;
  dodgeAir = false;
  readonly dodgeAxis = new THREE.Vector3(1, 0, 0);
  specialAge = 99;
  airT = 0;
  landAge = 99;
  landW = 0.6;
  airVy = 0;
  wasGrounded = true;
  jet = 0;
  nozHeat = 0;
  pvy = 0;
  psp = 0;
  ayS = 0;
  asS = 0;
  rollAxisFresh = true;
  dodgeDur = DODGE_DUR;
  /** 0 roll / air dash, 2 barrel roll, 3 hover flip: which spin the air dodge plays */
  dodgeSpin = 0;
  /** the barrel roll's turn: 1 rolls right (head over to the right), -1 left */
  barrelSign = 1;
  launchAge = 99;
  wasBarrel = false;
  heroAge = 99;
  wasHero = false;
  heroHold = HERO_HOLD;
  sprintW = 0;
  walkW = 1;
  adsW = 0;
  stallW = 0;
  freeRun = 0;
  cloak = 0;
  fire = 0;
  acid = 0;
  frame = 0;
  lod = 0;
  /** playtest 1: smoothed heat for the pack's glow; the Phantom's warp double image and its yellow wrap */
  heatS = 0;
  ghost: THREE.SkinnedMesh | null = null;
  ghostMat: THREE.MeshBasicMaterial | null = null;
  ghostAge = 99;
  ghostDur = 0.32;
  readonly ghostOffset = new THREE.Vector3();
  /** the form-fitting shell (Nick, playtest 2): yellow through the Phantom's warp / dash i-frames, shield-blue pulsing out from
   *  the impact when a hit lands on the shield. A skinned copy of the body, pushed out along its normals. */
  shell: THREE.SkinnedMesh | null = null;
  shellMat: THREE.MeshBasicMaterial | null = null;
  readonly shellU = { uHit: { value: new THREE.Vector3() }, uRing: { value: -1 }, uBase: { value: 0 } };
  /** the Dune shield (James, 2026-10-24): the shield and overshield skins over the body (fx/shieldskin.ts) */
  shieldSkin: ShieldSkin | null = null;
  readonly shState = { k: 0, over: 0, hx: 0, hy: 0, hz: 0, hitAge: 99, brkAge: 99, overBrkAge: 99, regenAge: 99, drainAge: 99, cloaked: false };
  readonly ghostWorld = new THREE.Vector3();
  dashGhostDone = false;
  lastWarpAge = 99;
  readonly lastPos = new THREE.Vector3();
  /** movement lane: the roll in progress is a slide; prone after an air dive */
  sliding = false;
  wasProne = false;
  proneAge = 99;
  proneW = 0;
  /** weight lane: the knockdown's body-local shove direction (right, forward) and its seed, latched at the knock */
  knockR = 0; knockF = 1; knockSeed = 0; lastKnockAge = 99;

  constructor() {
    this.model.rotation.y = Math.PI; // assets face +Z; the game's forward is -Z
    this.tumble.add(this.model);
    this.group.add(this.tumble);
  }

  build(a: CowboyAsset, cls: number, slot: number, quality: number) {
    this.disposeMeshes();
    const rig = buildRig(a, cls);
    this.rig = rig;
    this.cls = cls;
    const nB = a.nB;
    const bones: THREE.Bone[] = [];
    for (let i = 0; i < nB; i++) {
      const d = a.bones[i];
      const bn = new THREE.Bone();
      bn.name = d.n;
      bn.position.fromArray(d.pos);
      bn.quaternion.fromArray(d.q);
      bones.push(bn);
    }
    for (let i = 0; i < nB; i++) if (a.bones[i].p >= 0) bones[a.bones[i].p].add(bones[i]);
    const gun = new THREE.Bone();
    gun.name = 'Gun';
    bones.push(gun);
    // the pack bone (asset.ts re-skins the pack onto index nB + 1): a child of Spine2 at its joint, same bind
    const pack = new THREE.Bone();
    pack.name = 'Pack';
    bones[a.idx.Spine2].add(pack);
    bones.push(pack);
    this.bones = bones;
    this.gun = gun;
    // bind in model space with the model node at identity
    const holder = new THREE.Object3D();
    for (let i = 0; i <= nB; i++) if (i === nB || a.bones[i].p < 0) holder.add(bones[i]);
    holder.updateMatrixWorld(true);
    const skel = new THREE.Skeleton(bones);
    for (let i = 0; i <= nB; i++) if (bones[i].parent === holder) holder.remove(bones[i]);
    this.blade = new Blade(cls, a, this.bladeRoot);
    this.matSolid = cowboyMaterial(a, scarfColor(slot), false);
    this.matCloak = cowboyMaterial(a, scarfColor(slot), true);
    this.matCloak.depthWrite = true;
    const body = new THREE.SkinnedMesh(a.geo0, this.matSolid);
    for (let i = 0; i <= nB; i++) if (i === nB || a.bones[i].p < 0) body.add(bones[i]);
    body.bind(skel, new THREE.Matrix4());
    body.frustumCulled = false;
    body.castShadow = quality > 0;
    body.receiveShadow = true;
    body.name = 'cowboy-body';
    const outline = new THREE.SkinnedMesh(a.geo0, this.olSolid);
    outline.bind(skel, body.bindMatrix);
    outline.frustumCulled = false;
    outline.name = 'cowboy-outline';
    this.body = body; this.outline = outline;
    this.model.add(body, outline);
    this.shieldSkin = new ShieldSkin(body, (slot * 2.37 + cls * 0.61) % 7);

    this.acc = new Float32Array(nB * 4);
    this.wsum = new Float32Array(nB);
    this.modeMask = new Float32Array(nB);
    this.aimMask = new Float32Array(nB);
    this.ribbons = rig.ribbons.map((d) => {
      const j = a.bindW[d.bone];
      const rb = new Ribbon({ ...d, color: scarfColor(slot) }, [j.x, j.y, j.z] as V3, a.bindWq[d.bone].clone().invert());
      return rb;
    });
    this.fresh = true;
  }

  disposeMeshes() {
    this.shieldSkin?.dispose(); this.shieldSkin = null;
    if (this.body) {
      this.model.remove(this.body, this.outline!);
      this.body.skeleton.dispose();
      this.body = null; this.outline = null;
    }
    this.matSolid?.dispose(); this.matCloak?.dispose();
    this.matSolid = this.matCloak = null;
    this.blade?.dispose(); this.blade = null;
    this.bottle?.dispose(); this.bottle = null;
    this.ribbons = [];
    this.rig = null;
  }

  dispose() {
    this.disposeMeshes();
    this.olSolid.dispose(); this.olCloak.dispose();
    this.group.removeFromParent(); this.bladeRoot.removeFromParent();
  }

  // ---- pose helpers
  private barW = 0;
  private barClip: ClipData | undefined;
  private layer(c: ClipData | undefined, t: number, w: number, mask: Float32Array | null, hipW: number) {
    if (!c) return;
    accumulate(c, this.rig!.a.nB, this.rig!.a.fps * ANIM_PACE, t, w, mask, this.acc, this.wsum, this.hip, hipW); // a tenth slower
  }
  /** a locomotion cycle sampled by phase (0..1) over the WHOLE clip, whatever ANIM_PACE is (the paced sampler covered
   *  only 90% of each stride, so the feet slid and the stride popped back to frame 0 every cycle); also folds the
   *  clip's footfalls into this frame's gait (stride vector + contact per foot) */
  private layerLoco(c: ClipData | undefined, ph: number, w: number, mask: Float32Array | null) {
    if (!c || w <= 0.0001) return;
    accumulate(c, this.rig!.a.nB, this.rig!.a.fps, ph * c.dur, w, mask, this.acc, this.wsum, this.hip, w);
    const B = this.rig!.b;
    const g = gaitOf(this.rig!.a, c, B.LeftFoot, B.RightFoot);
    if (!g) return;
    this.gTx += g.tx * w; this.gTz += g.tz * w; this.gW += w;
    this.cL += contactAt(g, 0, ph) * w; this.cR += contactAt(g, 1, ph) * w; this.cW += w;
  }
  /** this stand's fidget (?fidget=<name> in the lab pins one through globalThis.__fidget) */
  private fidgetClip() {
    const pin = (globalThis as { __fidget?: string }).__fidget;
    return this.rig?.clip[pin ? (pin[0] === '@' ? pin.slice(1) : 'fidget_' + pin) : FIDGETS[(this.id + this.fidgetN) % FIDGETS.length]];
  }
  /** post-rotate bone i about a model-space axis (as seen in the T-pose frame) */
  private rFingers: number[] | null = null;
  private rot(i: number, axis: THREE.Vector3, ang: number) {
    if (i < 0 || Math.abs(ang) < 1e-5) return;
    const aq = this.rig!.a.aq[i];
    _qa.setFromAxisAngle(axis, ang);
    _q3.copy(aq).invert().multiply(_qa).multiply(aq);
    this.bones[i].quaternion.multiply(_q3);
  }
  /** the bottle rides the gun hand the way the gun grip would (its neck out of the top of the fist) */
  private whiskey(c: CowboyRS, da: number, alive: boolean) {
    const t = da / CHUG_DUR;
    const dd = DITCH[this.ditchNow], rel = FIN_START + dd.rel;
    const inHand = alive && t >= CHUG_DRAW && da < rel && !!this.rig!.clip.chug && this.group.visible;
    if (t < CHUG_DRAW) this.tossed = false;
    if (!inHand && !this.bottle?.group.visible) return;
    if (!this.bottle) { this.bottle = new Bottle(); this.bladeRoot.add(this.bottle.group); }
    const b = this.bottle, rig = this.rig!, a = rig.a;
    _m.compose(a.grip.pos, a.grip.q, ONE);
    _m2.multiplyMatrices(this.bones[a.grip.bone].matrixWorld, _m);
    _bP.setFromMatrixPosition(_m2); // the fist
    _bM.copy(rig.mouth).applyMatrix4(this.bones[rig.b.Head].matrixWorld);
    // base to neck: upright in the fist on the way up and down, tipped up with the neck at his lips while he swallows
    const g = chugGulpW(t);
    _bU.set(0, 1, 0).lerp(chugDir(this.bodyYaw, _bD), g).normalize();
    _q.setFromUnitVectors(Y_AXIS, _bU);
    // the fist wraps the body a third of the way up; while he drinks the neck is pulled the rest of the way to his lips
    _v.copy(_bP).addScaledVector(_bU, -BOTTLE_GRIP_Y);
    _v2.copy(_bM).sub(_v3.copy(_v).addScaledVector(_bU, BOTTLE_H));
    if (_v2.length() > 0.12) _v2.setLength(0.12);
    _v.addScaledVector(_v2, g);
    _m2.compose(_v, _q, ONE);
    // the draw: a quick scale-in out of the belt (Godot's materialize), not a blink
    const pop = clamp((t - CHUG_DRAW) / 0.07, 0, 1);
    _m2.decompose(b.group.position, b.group.quaternion, b.group.scale);
    b.group.scale.setScalar(0.35 + 0.65 * smooth(pop));
    b.group.updateMatrixWorld(true);
    b.fill(gulpFill(t, CHUG_GULP0, CHUG_GULP1));
    b.group.visible = inHand;
    if (!inHand && da >= rel && !this.tossed && alive && this.tosses) {
      this.tossed = true;
      const yaw = this.bodyYaw;
      _v.set(Math.cos(yaw), 0, -Math.sin(yaw)); // body right
      _v2.set(-Math.sin(yaw), 0, -Math.cos(yaw)); // body forward
      let spin = dd.spin;
      if (this.ditchNow === 0) {
        // the casual toss: off to his right and a little behind, a lazy flip end over end
        _v3.copy(_v).multiplyScalar(2.1 + Math.random() * 0.6).addScaledVector(_v2, -0.5 + Math.random() * 0.3);
        _v3.y = 2.4 + Math.random() * 0.5;
        _ikUp.copy(_v2).addScaledVector(_v, 0.3 * (Math.random() - 0.5));
        spin = -(9 + Math.random() * 4);
      } else {
        const j = 1 + (Math.random() - 0.5) * 0.15;
        _v3.copy(_v).multiplyScalar(dd.v[0] * j).addScaledVector(_v2, dd.v[2] * j);
        _v3.y = dd.v[1] * j;
        _ikUp.copy(dd.axis === 0 ? _v : _v2);
      }
      _v3.x += c.vx * 0.8; _v3.z += c.vz * 0.8;
      this.tosses.toss(b.group.matrixWorld, _v3, _ikUp, spin);
    }
  }
  /** melee lane (BladeHost): Cowboy.rot for the blade's chest twist and lean */
  rotBone(i: number, axis: THREE.Vector3, ang: number) { this.rot(i, axis, ang); }
  /** the gun goes to its holster: the melee lane's blade is drawn (or a swing has just started) */
  gunsAway(c: CowboyRS) { return !!c.blade || c.meleeAge < 0.3; }
  update(c: CowboyRS, dt: number, time: number, flames: THREE.InstancedMesh, flameIdx: number, camPos: THREE.Vector3, quality: number): number {
    const rig = this.rig!;
    const a = rig.a, B = rig.b, nB = a.nB;
    const T = CLS[this.cls & 3];
    this.frame++;
    const h = Math.hypot(c.vx, c.vz);
    const speed = Math.hypot(h, c.vy);
    const alive = c.phase === 0;

    // ---- triggers
    // the ditch is read until the finish starts (or a new drink begins), then held so a late event can't swap it mid-throw
    const da0 = c.drinkAge ?? 99;
    if (da0 < FIN_START || da0 < this.lastDa) this.ditchNow = (c.ditch ?? 0) % DITCH.length;
    this.lastDa = da0;
    const dk = c.dodgeK ?? (c.grounded ? 0 : 1);
    if (c.move === 4 && this.lastMove !== 4) {
      this.dodgeAge = 0; this.dodgeAir = dk !== 0 || this.baseMode === 3; this.dodgeSpin = dk === 3 ? 3 : dk === 1 ? 1 : 0;
      this.sliding = dk === 0 && !!c.slide;
      this.dodgeDur = dk === 0 ? (this.sliding ? SLIDE_DUR : ROLL_DUR) : dk === 3 ? HOVER_FLIP_DUR : dk === 1 ? DIVE_DUR : DODGE_DUR;
    }
    // the dive ends the moment it lands (the prone pose takes over); the sim ends the dodge the same tick
    if (this.dodgeSpin === 1 && c.move !== 4) this.dodgeAge = Math.max(this.dodgeAge, DIVE_DUR);
    // prone after a dive (LandK 3): flat on the belly, sliding, then the get-up; weight eases in and out
    const proneNow = c.landK === 3 && c.grounded && alive;
    if (proneNow && !this.wasProne) this.proneAge = 0;
    this.wasProne = proneNow;
    this.proneAge += dt;
    this.proneW = damp(this.proneW, proneNow ? 1 : 0, proneNow ? 18 : 6, dt);
    if (c.barrel && !this.wasBarrel) { this.dodgeAge = 0; this.dodgeAir = true; this.dodgeDur = BARREL_DUR; this.dodgeSpin = 2; this.barrelSign = (c.barrelSide ?? 1) < 0 ? -1 : 1; }
    // the launch: into flight from a jump, a fall or a hover (not back out of a barrel roll or a dash)
    if (c.move === 3 && this.lastMove !== 3 && this.lastMove !== 4 && !this.fresh) this.launchAge = 0;
    this.launchAge += dt;
    this.wasBarrel = !!c.barrel;
    // the superhero landing: the sim's hard landing, or the run-start drop (Saloon lane's scripted 3)
    const heroNow = (c.landK === 2 && c.grounded && alive) || c.scripted === 3;
    if (heroNow && !this.wasHero) { this.heroAge = 0; this.heroHold = c.scripted === 3 ? 0.6 : HERO_HOLD; }
    this.wasHero = heroNow;
    if (c.scripted === 3 && c.scriptedT !== undefined) this.heroAge = c.scriptedT; else this.heroAge += dt;
    if (c.move === 5 && this.lastMove !== 5) this.specialAge = 0;
    this.lastMove = c.move;
    this.dodgeAge += dt;
    this.specialAge += dt;
    // every touchdown lands, scaled by how hard it came in (a hop is a soft knee bend, a drop a real squash)
    if (c.grounded && !this.wasGrounded && this.airT > 0.12) { this.landAge = 0; this.landW = clamp(-this.airVy / 8, 0.3, 1); }
    if (!c.grounded) this.airVy = c.vy;
    this.airT = c.grounded ? 0 : this.airT + dt;
    this.wasGrounded = c.grounded;
    this.landAge += dt;

    // ---- mode weights
    let mode = this.baseMode;
    if (c.move !== 4 && c.move !== 5) mode = c.move === 3 ? 3 : c.move === 2 ? 2 : c.grounded ? 0 : 1;
    else if (c.grounded && mode !== 3) mode = 0;
    this.baseMode = mode;
    let target = mode;
    if (c.phase >= 1) target = 4;
    if (c.skyT > 0.001) target = 5;
    if (this.fresh) { this.w.fill(0); this.w[target] = 1; }
    let ws = 0;
    for (let i = 0; i < 6; i++) {
      const rate = i === 3 || target === 3 ? 8 : 11;
      this.w[i] = damp(this.w[i], i === target ? 1 : 0, rate, dt);
      ws += this.w[i];
    }
    const iw = 1 / ws;
    const wG = this.w[0] * iw, wA = this.w[1] * iw, wH = this.w[2] * iw, wF = this.w[3] * iw, wD = this.w[4] * iw, wS = this.w[5] * iw;
    this.dead = damp(this.dead, c.phase === 2 ? 1 : 0, 5, dt);
    this.deadT = c.phase === 2 ? this.deadT + dt : 0;
    this.downT = c.phase === 1 ? this.downT + dt : 0;

    // ---- facing
    const flying = c.move === 3 && h > 2;
    // free-run: with the gun relaxed (or sprinting) the cowboy faces where he runs, like any modern
    // third-person shooter; aiming, shooting, reloading or hovering snaps him back to strafing on the aim
    const freeRunNow = c.grounded && h > 1.2 && !c.ads && c.move !== 4 && c.move !== 6 && (c.sprint || (this.ready < 0.3 && !c.reloading)) && !(c.meleeAge < 0.7);
    this.freeRun = damp(this.freeRun, freeRunNow ? 1 : 0, freeRunNow ? 6 : 14, dt);
    const heading = flying || this.freeRun > 0.5 ? Math.atan2(-c.vx, -c.vz) : c.yaw;
    if (this.fresh) this.bodyYaw = heading;
    const dodging = this.dodgeAge < this.dodgeDur + 0.04;
    // an air spin (dash, barrel roll, hover flip) turns the whole body, so the gun rides in the hand through it
    // instead of holding its aim or carry pose in the unspun frame (where it would hang in the air off the body)
    const spinT = this.dodgeAge / this.dodgeDur;
    const spinW = dodging && this.dodgeAir ? clamp(Math.min(spinT, 1 - spinT) * 6, 0, 1) : 0;
    const prevYaw = this.bodyYaw;
    // turn in place: standing still, the spine soaks up the aim yaw (up to ~55 deg) and the feet stay planted;
    // past that the cowboy steps round to square up again, instead of spinning on the spot every frame
    const standing = c.grounded && h < 0.6 && c.move === 0 && !dodging;
    const yawGap = wrapPi(heading - this.bodyYaw);
    if (standing && !this.fresh) {
      if (Math.abs(yawGap) > 0.95) this.turning = true;
      else if (Math.abs(yawGap) < 0.06) this.turning = false;
    } else this.turning = false;
    this.turnT = this.turning ? this.turnT + dt : 0;
    if (standing && !this.turning && !this.fresh) { /* hold the feet */ }
    else if (!dodging || flying) this.bodyYaw += wrapPi(heading - this.bodyYaw) * (1 - Math.exp(-(flying ? 7 : this.freeRun > 0.5 ? 11 : this.turning ? 9 : 16) * dt));
    this.bodyYaw = wrapPi(this.bodyYaw);
    const yr = dt > 0 ? wrapPi(this.bodyYaw - prevYaw) / dt : 0;
    this.yawRate = damp(this.yawRate, yr, 10, dt);
    const sy = Math.sin(this.bodyYaw), cy = Math.cos(this.bodyYaw);
    const fwdS = -(c.vx * sy + c.vz * cy);
    const rightS = c.vx * cy - c.vz * sy;

    // ---- smoothed scalars
    // a slow walk is the walk clip at a slower cadence, not half an idle (feet half-standing, half-striding skated)
    this.runAmt = damp(this.runAmt, c.grounded ? Math.min(h / 1.3, 1) : 0, 9, dt);
    if (h > 0.5) {
      const ang = Math.atan2(rightS, fwdS);
      this.dirAng += wrapPi(ang - this.dirAng) * (1 - Math.exp(-10 * dt));
    } else this.dirAng += wrapPi(0 - this.dirAng) * (1 - Math.exp(-3 * dt));
    this.sprintW = damp(this.sprintW, c.sprint && c.grounded && h > 5 ? 1 : 0, 7, dt);
    this.walkW = damp(this.walkW, 1 - smooth(clamp((h - 2.4) / 2.4, 0, 1)), 10, dt);
    this.adsW = damp(this.adsW, c.ads && alive && c.move !== 3 ? 1 : 0, 14, dt);
    this.stallW = damp(this.stallW, c.stall ? 1 : 0, c.stall ? 10 : 4, dt);
    // feet stay planted: the phase advances by distance over the clips' own travel per cycle. Past 1.4x a
    // clip's natural cadence the legs would blur, so the cadence caps there and the stride lengthens instead (strideK)
    const ck = this.rig!.clip;
    const trav = (cl: ClipData | undefined, d: number) => cl?.travel || d;
    const sW = this.walkW * (1 - this.sprintW), sR = (1 - this.walkW) * (1 - this.sprintW), sS = this.sprintW;
    // the stride is the last frame's blend of the clips actually playing (per direction: a strafe or a backpedal
    // covers different ground per cycle than the forward run); before any has played, the forward clips' travel
    const stride = this.gStride > 0.3 ? this.gStride : trav(ck.walk_f, 1.94) * sW + trav(ck.run_f, 2.43) * sR + trav(ck.sprint_f, 3.64) * sS;
    const crawl = c.phase === 1 && !!ck.cwalk_f;
    const natHz = crawl ? 1 / ck.cwalk_f!.dur : sW / (ck.walk_f?.dur ?? 1) + sR / (ck.run_f?.dur ?? 0.5) + sS / (ck.sprint_f?.dur ?? 0.5);
    // a slow walk is part standing pose, which shortens the stride the feet actually draw by the same share
    const drawn = stride * Math.max(0.25, this.runAmt);
    // (a sprint may turn over faster: it's meant to look frantic, and stretching the stride instead lifts the feet)
    const hz = Math.min(Math.max(0.45, h / drawn), natHz * (1.4 + 0.2 * sS));
    this.strideHz = hz;
    this.strideK = damp(this.strideK, clamp(h / (hz * drawn), 1, 1.35), 8, dt);
    if (c.grounded) this.runPhase = (this.runPhase + dt * hz * (c.phase === 1 && !crawl ? 0.6 : 1)) % 1;
    // a ground roll tucks the gun away (slung / holstered) so a long barrel never sticks up out of the ball; so does
    // sitting at the bar (the drink hand is the gun hand)
    const rolling = this.dodgeAge < ROLL_DUR * 0.85 && !this.dodgeAir;
    // weapon swap: the gun in hand goes to its stow (holster or back), the new one comes out of its own. The view
    // shows `shown` until the old gun is fully away, so the swap reads as put-away -> draw
    const cur = c.weapon !== undefined && c.weapon >= 0 ? c.weapon : -1;
    if (this.fresh || this.shown === -2) { this.shown = cur; this.hold = holdOf(cur, a.pistol); }
    const swapping = this.shown !== cur;
    const wantHolster = rolling || swapping || this.gunsAway(c) || c.holstered || (c.throwAge ?? 99) < THROW_DUR * 0.75 || (c.drinkAge ?? 99) < FIN_START + DITCH[this.ditchNow].end || c.move === 3 || c.skyT > 0.05 || c.phase === 2 || c.scripted === 1 || c.scripted === 2 || (c.move === 5 && this.cls === 1 && !c.grounded) || this.fidgetAge >= 0 ? 1 : 0;
    // a swap paces itself from sim/weapons `draw`: the outgoing gun's for putting away, the incoming one's for the
    // draw (a sling takes longer than a holster)
    const outS = swapping ? this.drawOf(this.shown) : 0;
    const floorS = this.hold.stow === StowK.Back ? 0.22 : 0.12;
    this.holster = wantHolster ? Math.min(1, this.holster + dt / (swapping ? Math.max(floorS, outS * 0.45) : 0.14))
      : Math.max(0, this.holster - dt / (this.drawing ? Math.max(floorS + 0.02, this.swapDraw * 0.55) : 0.16));
    if (this.fresh) this.holster = wantHolster;
    if (swapping) {
      this.swapDraw = this.drawOf(cur);
      if (this.holster >= 0.999) { this.shown = cur; this.hold = holdOf(cur, a.pistol); this.drawing = true; }
    } else if (this.holster <= 0.001) this.drawing = false;
    this.gunStowed = this.holster;
    this.ready = damp(this.ready, (c.shotAge < 2.5 || c.move === 2 || c.reloading || !c.grounded || c.ads) && !(c.sprint && c.shotAge > 0.3) ? 1 : 0, c.shotAge < 0.05 || c.ads ? 40 : 4, dt);
    if (this.fresh) this.ready = c.shotAge < 2.5 || c.move === 2 || !c.grounded ? 1 : 0;
    this.reload = damp(this.reload, c.reloading ? 1 : 0, 12, dt);
    // stood still and relaxed for a while: now and then a fidget (Godot's flavor idles: 7 s in, 4 s apart). The gun
    // is put away for it so the whole body plays the clip (a neck crack, a hat tip); any action ends it and redraws
    const still = c.grounded && alive && h < 0.3 && this.ready < 0.1 && !c.ads && !c.reloading && !c.scripted && !c.blade && (c.drinkAge ?? 99) > 6;
    const fc = this.fidgetClip();
    if (!still) { this.stillT = 0; this.fidgetAge = -1; }
    else if (this.fidgetAge >= 0) { this.fidgetAge += dt; if (!fc || this.fidgetAge > fc.dur) { this.fidgetAge = -1; this.stillT = 3; this.fidgetN++; } }
    else if ((this.stillT += dt) > 7 && fc) this.fidgetAge = 0;
    this.reloadT = c.reloading ? this.reloadT + dt : 0;
    this.cloak = damp(this.cloak, c.cloaked ? 1 : 0, 7, dt);
    this.fire = damp(this.fire, c.onFire ? 1 : 0, 8, dt);
    const velPitch = Math.atan2(c.vy, Math.max(h, 0.01));
    this.flightPitch = damp(this.flightPitch, clamp(-Math.PI / 2 + 0.14 + velPitch * 0.85, -Math.PI + 0.25, -0.35), 8, dt);
    const bankT = clamp(-this.yawRate * 0.42, -1.15, 1.15) * wF;
    const nb = damp(this.bank, bankT, 6, dt);
    this.bankRate = dt > 0 ? (nb - this.bank) / dt : 0;
    this.bank = nb;
    this.armLag = damp(this.armLag, clamp(this.bankRate * 0.25, -0.5, 0.5), 7, dt);

    // ---- overrides (0..1) that take the pose away from the base layers
    let oRoll = 0, rollT = 0, oSlide = 0;
    const dashing = this.cls === 2 && !this.dodgeAir && !this.sliding && this.dodgeAge < 0.34 / ANIM_PACE; // the Phantom dashes, never rolls (CONTROLS.md)
    if (this.dodgeAge < ROLL_DUR && !this.dodgeAir && !this.sliding && this.cls !== 2) {
      // the roll, compressed into the dodge, then a short recovery blend back to the base
      const t = clamp(this.dodgeAge / ROLL_DUR, 0, 1);
      oRoll = Math.min(1, t * 8) * (1 - smooth(clamp((t - 0.72) / 0.28, 0, 1)));
      rollT = 0.15 + t * 1.75;
    } else if (this.dodgeAge < SLIDE_DUR + 0.15 && !this.dodgeAir && this.sliding) {
      // the slide: drops onto one hip and rides it out, then stands back up into the run
      const t = clamp(this.dodgeAge / (SLIDE_DUR + 0.15), 0, 1);
      oSlide = Math.min(1, t * 10) * (1 - smooth(clamp((t - 0.8) / 0.2, 0, 1)));
    }
    const oProne = this.proneW;
    // superhero landing envelope: slams in, holds planted, rises
    let oHero = 0;
    if (this.heroAge < this.heroHold + HERO_RISE) {
      const ta = this.heroAge;
      oHero = Math.min(1, ta / 0.05) * (1 - smooth(clamp((ta - this.heroHold) / HERO_RISE, 0, 1)));
    }
    // grenade throw (upper body), mirrored timing for every class
    let oThrow = 0, throwT = 0;
    const ta = c.throwAge ?? 99;
    if (ta < THROW_DUR && alive) {
      const t = ta / THROW_DUR;
      oThrow = Math.min(1, t * 10) * (1 - smooth(clamp((t - 0.7) / 0.3, 0, 1)));
      throwT = THROW_WIN[0] + (THROW_WIN[1] - THROW_WIN[0]) * t;
    }
    // the whiskey chug (upper body over walking legs): fades in on the belt grab, out as the arm comes down
    let oChug = 0, chugT = 0;
    const da = c.drinkAge ?? 99;
    if (da < CHUG_DUR && alive && rig.clip.chug) {
      const t = da / CHUG_DUR;
      oChug = Math.min(1, t * 9) * (1 - smooth(clamp((t - 0.86) / 0.14, 0, 1)));
      chugT = da * CHUG_SPEED;
    }
    let oMelee = 0, meleeT = 0;
    if (c.meleeAge < 0.45 && alive && !this.blade) { // every melee draws the class blade now (blades.ts poses it)
      const t = c.meleeAge / 0.45;
      oMelee = Math.min(1, Math.sin(Math.PI * t) * 1.8);
      const cl = rig.clip[T.melee];
      meleeT = cl ? (T.mWin[0] + (T.mWin[1] - T.mWin[0]) * t) * cl.dur : 0;
    }
    const oDeath = this.dead;
    const oReload = this.reload * (1 - this.holster);
    const hold = this.hold;
    // scripted bar poses (Saloon lane): 1 sit, 2 eat, faded in/out over ~0.25 s
    const barClip = c.scripted === 1 ? rig.clip.bar_sit : c.scripted === 2 ? rig.clip.bar_eat : undefined;
    this.barW += ((barClip ? 1 : 0) - this.barW) * Math.min(1, dt * 8);
    if (barClip) this.barClip = barClip;
    const oBar = this.barClip ? this.barW : 0;
    // weight lane: knocked off his feet. KnockKind 1: tips over along the shove and lies flat, then gets up.
    // KnockKind 2 (a ragdoll): flung along the shove first, limbs loose, lands and lies, then gets up. Timings
    // follow the sim (defs.ts KNOCKDOWN 0.7 s + GETUP 0.45 s); the fling is the client's own 0.45 s on top.
    const ka = c.knockAge ?? 99;
    const kRag = c.knockKind === 2;
    const kFling = kRag ? KNOCK_FLING : 0;
    const kLen = kFling + KNOCK_LIE + KNOCK_GETUP;
    let oKnock = 0, kFall = 0, kLie = 0, kUp = 0, kFly = 0;
    if (ka < kLen && alive) {
      if (ka < this.lastKnockAge || this.lastKnockAge >= 99) {
        // latch the shove in the body frame (right = (cos yaw, -sin yaw), forward = (-sin yaw, -cos yaw))
        const dx = c.knockDX ?? 0, dz = c.knockDZ ?? 1;
        this.knockR = dx * Math.cos(c.yaw) - dz * Math.sin(c.yaw);
        this.knockF = -dx * Math.sin(c.yaw) - dz * Math.cos(c.yaw);
        const l = Math.hypot(this.knockR, this.knockF) || 1;
        this.knockR /= l; this.knockF /= l;
        this.knockSeed = (this.id * 7.31 + ka * 13) % 1;
      }
      oKnock = Math.min(1, ka * 12) * (1 - smooth(clamp((ka - (kLen - 0.12)) / 0.12, 0, 1)));
      kFly = kRag ? 1 - smooth(clamp((ka - kFling * 0.8) / (kFling * 0.2), 0, 1)) : 0;
      const tf = ka - kFling;
      kFall = smooth(clamp(tf / 0.22, 0, 1));
      kLie = tf < KNOCK_LIE ? 1 : 1 - smooth(clamp((tf - KNOCK_LIE) / KNOCK_GETUP, 0, 1));
      kUp = tf > KNOCK_LIE ? smooth(clamp((tf - KNOCK_LIE) / KNOCK_GETUP, 0, 1)) : 0;
    }
    this.lastKnockAge = ka;
    const base = (1 - oRoll) * (1 - oDeath) * (1 - oHero) * (1 - oBar) * (1 - oKnock * 0.85) * (1 - oSlide * 0.8) * (1 - oProne * 0.85);

    // ---- sample the layers
    const acc = this.acc, wsum = this.wsum, hip = this.hip;
    acc.fill(0); wsum.fill(0); hip.fill(0);
    this.gTx = 0; this.gTz = 0; this.gW = 0; this.cL = 0; this.cR = 0; this.cW = 0;
    const aimW = (wG + wA + wH + wD * 0.6) * (1 - this.holster) * (1 - oMelee * (T.mFull ? 1 : 0.85)) * (1 - oReload) * (1 - oThrow) * (1 - oChug) * (1 - oHero) * (1 - oBar);
    const mm = this.modeMask, am = this.aimMask;
    for (let i = 0; i < nB; i++) { mm[i] = 1 - rig.upper[i] * aimW; am[i] = rig.upper[i] * aimW; }
    const aimClip = hold.pistolAim ? rig.clip.idle_p : rig.clip.idle_r;

    // ground: idle <-> 8-way run
    if (wG > 0.001) {
      const ra = this.runAmt;
      // a fidget takes over the stand (crossfaded in and out), the idle fills the rest
      const fc = this.fidgetAge >= 0 ? this.fidgetClip() : undefined;
      const fk = fc ? smooth(clamp(Math.min(this.fidgetAge / 0.6, (fc.dur - this.fidgetAge) / 0.9), 0, 1)) : 0;
      const sw = wG * (1 - ra) * base;
      this.layer(aimClip, time, sw * (1 - fk), mm, sw * (1 - fk));
      if (fk > 0.001) this.layer(fc, this.fidgetAge, sw * fk, null, sw * fk);
      if (ra > 0.001) {
        // two nearest directional clips per set; walk <-> run by speed, sprint on top (forward arc only)
        const ang = this.dirAng;
        const k = ((ang / (Math.PI / 4)) % 8 + 8) % 8;
        const k0 = Math.floor(k), f = k - k0;
        const haveWalk = !!rig.clip.walk_f, haveSprint = !!rig.clip.sprint_f;
        const sw = haveSprint ? this.sprintW : 0;
        const ww = haveWalk ? this.walkW * (1 - sw) : 0;
        const rw = (1 - ww) * (1 - sw);
        const ph = this.runPhase;
        const set = (names: string[], wSet: number) => {
          if (wSet < 0.001) return;
          const c0 = rig.clip[names[k0]], c1 = rig.clip[names[(k0 + 1) % 8]];
          const w0 = wG * ra * (1 - f) * base * wSet, w1 = wG * ra * f * base * wSet;
          this.layerLoco(c0, ph, w0, mm);
          this.layerLoco(c1, ph, w1, mm);
        };
        set(RUN8, rw);
        if (ww > 0.001) set(WALK8, ww);
        if (sw > 0.001) {
          // sprint has f / fl / fr: fold the direction into that arc
          const da = clamp(ang / (Math.PI / 4), -1, 1);
          const side = da < 0 ? rig.clip.sprint_fl : rig.clip.sprint_fr;
          const fa = Math.abs(da);
          const w0 = wG * ra * base * sw;
          this.layerLoco(rig.clip.sprint_f, ph, w0 * (1 - fa), mm);
          if (side && fa > 0.001) this.layerLoco(side, ph, w0 * fa, mm);
        }
      }
      // landing squash
      if (this.landAge < 0.5) { const lw = wG * this.landW * (1 - this.landAge / 0.5) * base; this.layer(rig.clip.land, 0.12 + this.landAge, lw, mm, lw); }
    }
    if (this.gW > 0.001) this.gStride = Math.hypot(this.gTx, this.gTz) / this.gW;
    if (wA > 0.001) {
      const up = clamp((c.vy + 2) / 8, 0, 1);
      this.layer(rig.clip.jump, 0.25 + Math.min(this.airT, 0.25), wA * up * base, mm, wA * up * base);
      this.layer(rig.clip.fall, time, wA * (1 - up) * base, mm, wA * (1 - up) * base);
    }
    // falling: the legs reach down for the ground instead of holding the rising tuck (rising and falling read apart)
    const reach = wA * clamp((-c.vy - 1) / 8, 0, 1) * base;
    const rise = wA * clamp((c.vy - 1) / 5, 0, 1) * base;
    // hover: standing aim pose, legs let go (procedural dangle below)
    if (wH > 0.001) this.layer(aimClip, time + this.id * 0.37, wH * base, mm, wH * base);
    if (wF > 0.001) accumulateStatic(a.tpose, nB, wF * base, null, acc, wsum);
    if (wD > 0.001) {
      // downed: the crouch, stepping on the 8-way crouched walk when crawling (its feet plant like the run's); a
      // cowboy without the side file falls back to the crouch pose
      const ra = rig.clip.cwalk_f ? this.runAmt : 0;
      this.layer(rig.clip.crouch, time * (h > 0.3 && !ra ? 1.6 : 1), wD * (1 - ra) * base, mm, wD * (1 - ra) * base);
      if (ra > 0.001) {
        const k = ((this.dirAng / (Math.PI / 4)) % 8 + 8) % 8, k0 = Math.floor(k), f = k - k0;
        this.layerLoco(rig.clip[CWALK8[k0]], this.runPhase, wD * ra * (1 - f) * base, mm);
        this.layerLoco(rig.clip[CWALK8[(k0 + 1) % 8]], this.runPhase, wD * ra * f * base, mm);
      }
    }
    if (wS > 0.001) this.layer(rig.clip.fall, time * 0.4 + this.id, wS * base, null, wS * base);
    // aim pose on the upper body (shared time so the arms are steady)
    this.layer(aimClip, time, base, am, 0);
    // full-body overrides
    // the roll body: a crouch base, curled into a ball procedurally below (the Mixamo roll plants its hands and
    // read as crawling once the whole body rotates)
    if (oRoll > 0.001) this.layer(rig.clip.crouch ?? rig.clip.roll, rig.clip.crouch ? 0 : rollT, oRoll * (1 - oDeath), null, oRoll * (1 - oDeath));
    if (HERO_CROUCH && oHero > 0.001 && rig.clip.crouch) this.layer(rig.clip.crouch, 0.2, oHero * (1 - oDeath), null, oHero * (1 - oDeath));
    else if (oHero > 0.001) {
      // a real base under the procedural hero pose: blending out of it must crossfade, not renormalise a near-zero
      // clip weight up to full (that popped the whole body in one frame as the rise began)
      const hw = oHero * (1 - oDeath);
      accumulateStatic(rig.bind, nB, hw, null, acc, wsum);
      const hp = a.bones[B.Hips].pos;
      this.hip[0] += hp[0] * hw; this.hip[1] += hp[1] * hw; this.hip[2] += hp[2] * hw; this.hip[3] += hw;
    }
    if (oBar > 0.001 && this.barClip) this.layer(this.barClip, c.scriptedT ?? time, oBar * (1 - oDeath), null, oBar * (1 - oDeath));
    if (oDeath > 0.001) this.layer(rig.clip.death, 0.4 + this.deadT, oDeath, null, oDeath);
    if (oKnock > 0.001 && rig.clip.crouch) { const kw = oKnock * 0.85 * (1 - oDeath); this.layer(rig.clip.crouch, 0.1, kw, null, kw); }
    if (oSlide > 0.001 && rig.clip.crouch) { const sw = oSlide * 0.8 * (1 - oDeath); this.layer(rig.clip.crouch, 0.15, sw, null, sw); }
    if (oProne > 0.001) { const pw = oProne * 0.85 * (1 - oDeath); this.layer(rig.clip.fall, 0.2, pw, null, pw); }

    // normalise into the bones
    const Bn = this.bones;
    for (let i = 0; i < nB; i++) {
      const o = i * 4;
      let x = acc[o], y = acc[o + 1], z = acc[o + 2], w = acc[o + 3];
      const l = Math.hypot(x, y, z, w);
      if (wsum[i] < 1e-4 || l < 1e-6) { const d = a.bones[i].q; x = d[0]; y = d[1]; z = d[2]; w = d[3]; }
      else { x /= l; y /= l; z /= l; w /= l; }
      Bn[i].quaternion.set(x, y, z, w);
    }
    // upper-body overrides blended on top (melee / reload) with a slerp per bone
    if (oThrow > 0.001 && rig.clip.throw) {
      acc.fill(0); wsum.fill(0);
      const cl = h > 2 && c.grounded && rig.clip.throw_run ? rig.clip.throw_run : rig.clip.throw;
      accumulate(cl, nB, a.fps, throwT, 1, rig.upper, acc, wsum, _hipTmp, 0);
      for (let i = 0; i < nB; i++) {
        if (wsum[i] < 1e-4) continue;
        const o = i * 4;
        _q.set(acc[o], acc[o + 1], acc[o + 2], acc[o + 3]).normalize();
        Bn[i].quaternion.slerp(_q, clamp(oThrow * rig.upper[i], 0, 1));
      }
    }
    if (oChug > 0.001) {
      acc.fill(0); wsum.fill(0);
      accumulate(rig.clip.chug, nB, a.fps, chugT, 1, rig.upper, acc, wsum, _hipTmp, 0);
      for (let i = 0; i < nB; i++) {
        if (wsum[i] < 1e-4) continue;
        const o = i * 4;
        _q.set(acc[o], acc[o + 1], acc[o + 2], acc[o + 3]).normalize();
        Bn[i].quaternion.slerp(_q, clamp(oChug * rig.upper[i], 0, 1));
      }
    }
    if (oMelee > 0.001) {
      acc.fill(0); wsum.fill(0);
      const cl = rig.clip[T.melee];
      const mask = T.mFull ? null : rig.upper;
      const ov = oMelee;
      if (cl) accumulate(cl, nB, a.fps, meleeT, oMelee, mask, acc, wsum, _hipTmp, 0);
      for (let i = 0; i < nB; i++) {
        if (wsum[i] < 1e-4) continue;
        const o = i * 4;
        _q.set(acc[o], acc[o + 1], acc[o + 2], acc[o + 3]).normalize();
        Bn[i].quaternion.slerp(_q, clamp(ov * (mask ? mask[i] : 1), 0, 1));
      }
    }
    // melee lane: the flourish's mocap body (blades.ts MOCAP_BODY) under the blade's IK arms; the hips' step-in comes too
    this.bodyW = 0;
    if (this.blade && alive) {
      const bd = this.blade.bodyAt(c, this.runAmt, _body);
      const cl = bd && rig.clip[bd.clip];
      if (cl) {
        acc.fill(0); wsum.fill(0); _hipTmp.fill(0);
        accumulate(cl, nB, a.fps, bd.t, 1, rig.noArms, acc, wsum, _hipTmp, 1);
        for (let i = 0; i < nB; i++) {
          if (wsum[i] < 1e-4) continue;
          const o = i * 4;
          _q.set(acc[o], acc[o + 1], acc[o + 2], acc[o + 3]).normalize();
          Bn[i].quaternion.slerp(_q, clamp(bd.w * rig.noArms[i], 0, 1));
        }
        this.bodyW = bd.w;
        this.bodyHip.set(_hipTmp[0], _hipTmp[1], _hipTmp[2]);
      }
    }
    // relaxed arms when the gun has been quiet a while (aim pose comes back on the first shot)
    // quiet gun: the CARRY pose (holds.ts) replaces the aim; the gun is placed in the body frame and the hands are
    // IK'd onto it below. Only a one-handed hold's free left arm relaxes (the old both-arms relax read as an A-pose)
    const relax = (1 - this.ready) * wG * (1 - oReload) * (1 - oMelee) * (1 - oChug) * (1 - this.holster) * base;
    if (relax > 0.001 && !hold.twoHand && hold.k !== HoldK.Akimbo) {
      for (const bi of [B.LeftArm, B.LeftForeArm]) {
        if (bi < 0) continue;
        _q.fromArray(rig.relaxed, bi * 4);
        Bn[bi].quaternion.slerp(_q, relax);
      }
    }
    // stride warp: a long stride at a capped cadence swings the thighs further about the standing pose
    const warp = (this.strideK - 1) * wG * this.runAmt * base;
    if (warp > 0.001) {
      for (const [bi, gain] of [[B.LeftUpLeg, 1], [B.RightUpLeg, 1], [B.LeftLeg, 0.5], [B.RightLeg, 0.5]] as const) {
        if (bi < 0) continue;
        const qb = Bn[bi].quaternion;
        _qa.fromArray(a.tpose, bi * 4);
        _q.copy(_qa).invert().multiply(qb); // deviation from standing
        if (_q.w < 0) _q.set(-_q.x, -_q.y, -_q.z, -_q.w);
        const ang = 2 * Math.acos(Math.min(1, _q.w));
        if (ang < 1e-4) continue;
        const sc = Math.sin(ang * (1 + warp * gain) / 2) / Math.sin(ang / 2);
        _q.set(_q.x * sc, _q.y * sc, _q.z * sc, Math.cos(ang * (1 + warp * gain) / 2));
        qb.copy(_qa).multiply(_q);
      }
    }
    // hips position (model space), feet stay planted for root clips
    const hipsB = Bn[B.Hips];
    if (hip[3] > 1e-4) hipsB.position.set(hip[0] / hip[3], hip[1] / hip[3], hip[2] / hip[3]);
    else hipsB.position.fromArray(a.bones[B.Hips].pos);
    if (this.bodyW > 0.001) hipsB.position.lerp(this.bodyHip, this.bodyW);
    // a wider stride lowers the hips (the legs reach further forward and back, so less of them is under the body);
    // without it the stretched stride runs on air
    if (warp > 0.001) hipsB.position.y -= warp * 0.12;
    if (wF > 0.001) {
      // flight: hips stay at the rest height (the tumble pivots the body)
      _v.fromArray(a.bones[B.Hips].pos);
      hipsB.position.lerp(_v, wF);
    }

    // ---- procedural layers
    // aim pitch through the spine (upper body only; the gun is aimed exactly below)
    const p = clamp(c.pitch, -1.2, 1.2) * aimW;
    const low = (1 - this.ready) * 0.25;
    this.rot(B.Spine, X_AXIS, -(p * 0.25 - low * 0.3));
    this.rot(B.Spine1, X_AXIS, -p * 0.3);
    this.rot(B.Spine2, X_AXIS, -p * 0.3);
    this.rot(B.Head, X_AXIS, -p * 0.2);
    // untwist the bladed Mixamo stance: turn the planted hips back toward the aim line and the
    // chest the rest of the way, so the pack and shoulders face where the cowboy shoots
    {
      const idleW = (wG * (1 - this.runAmt) + wH) * base * (1 - this.holster * 0.5);
      if (idleW > 0.001) { _qa.setFromAxisAngle(Y_AXIS, -rig.twist.hips * 0.8 * idleW); Bn[B.Hips].quaternion.premultiply(_qa); }
      const un = -(rig.twist.torso - rig.twist.hips) * 0.6 * aimW;
      this.rot(B.Spine, Y_AXIS, un * 0.3); this.rot(B.Spine1, Y_AXIS, un * 0.3); this.rot(B.Spine2, Y_AXIS, un * 0.4);
      this.rot(B.Neck, Y_AXIS, -un * 0.9);
      // quiet gun (standing about, guns at the carry or holstered): square the chest to the facing, measured from
      // the pose as it is (whatever clip is playing), so the shoulders and the pack sit straight behind the cowboy
      // instead of riding a bladed idle off to one side
      // the hips first: several clips (the holstered idle, the walks) carry a standing 20-35 deg turn of the whole
      // lower body, so from the chase camera the cowboy looked turned and his pack hung off one side. Take out the
      // hips' AVERAGE yaw (smoothed over ~a second), never the stride's own swing
      const hq = wG * base * (1 - oRoll);
      let hipSwing = 0;
      if (B.Hips >= 0) {
        this.body!.updateMatrixWorld(true);
        this.body!.getWorldQuaternion(_qa);
        Bn[B.Hips].getWorldQuaternion(_q3);
        _q2.copy(_qa).invert().multiply(_q3).multiply(_iw.copy(a.bindWq[B.Hips]).invert());
        _e.setFromQuaternion(_q2, 'YXZ');
        this.hipYaw = this.fresh ? _e.y : damp(this.hipYaw, _e.y, 2.5, dt);
        hipSwing = _e.y - this.hipYaw;
        if (hq > 0.001) {
          turnWorld(Bn[B.Hips], -this.hipYaw * 0.9 * hq);
          // on the move the legs keep the clip's own line: the pelvis squares up, but the thighs turn back so the
          // stride still runs along the travel (turning the whole lower body swung each planted foot ~40 cm sideways
          // through its stance, a skate the eye reads at once)
          const lk = this.hipYaw * 0.9 * hq * this.runAmt;
          if (Math.abs(lk) > 1e-4) { turnWorld(Bn[B.LeftUpLeg], lk); turnWorld(Bn[B.RightUpLeg], lk); }
        }
      }
      // then the chest, the same way (its average, so the shoulders still swing with the stride)
      const sq = (1 - aimW) * wG * base * (1 - oRoll);
      if (B.Spine1 >= 0 && B.Spine2 >= 0) {
        this.body!.getWorldQuaternion(_qa);
        Bn[B.Spine2].getWorldQuaternion(_q3);
        _q2.copy(_qa).invert().multiply(_q3).multiply(_iw.copy(a.bindWq[B.Spine2]).invert());
        _e.setFromQuaternion(_q2, 'YXZ');
        this.chestYaw = this.fresh ? _e.y : damp(this.chestYaw, _e.y, 2.5, dt);
        if (sq > 0.001) { const yaw = -this.chestYaw * 0.85 * sq; turnWorld(Bn[B.Spine1], yaw * 0.5); turnWorld(Bn[B.Spine2], yaw * 0.5); }
      }
      // a runner's shoulders swing against the pelvis: the rifle clips hold the chest still behind the gun, which
      // read as robotic (critics, round 3). The counter-swing is the clip's own pelvis swing, mirrored, so it keeps
      // time with the stride; the neck takes it back so the head and the aim stay steady
      const cs = clamp(-hipSwing * 1.8, -0.25, 0.25) * hq * this.runAmt * (1 - this.adsW * 0.6);
      if (Math.abs(cs) > 1e-4) { turnWorld(Bn[B.Spine1], cs * 0.4); turnWorld(Bn[B.Spine2], cs * 0.6); this.rot(B.Neck, Y_AXIS, -cs * 0.8); }
    }
    const breathe = Math.sin(time * 1.7 + this.id) * (1 - this.runAmt) * wG;
    this.rot(B.Spine2, X_AXIS, -0.025 * breathe);
    // standing about: a slow weight shift from hip to hip (the planted feet hold, so the legs take it up), the pelvis
    // tilting over the loaded leg and the chest counter-rolling, so a cowboy at rest is never a statue
    {
      const st = (1 - this.runAmt) * wG * base * (1 - this.adsW * 0.7);
      if (st > 0.001 && B.Hips >= 0) {
        const ws = Math.sin(time * 1.45 + this.id * 2.1) * st, wv = Math.sin(time * 2.9 + this.id * 2.1) * st;
        Bn[B.Hips].position.x += 0.013 * ws;
        Bn[B.Hips].position.y -= 0.005 * Math.abs(wv);
        this.rot(B.Hips, Z_AXIS, 0.025 * ws);
        this.rot(B.Spine1, Z_AXIS, -0.02 * ws);
        this.rot(B.Head, Z_AXIS, -0.01 * ws);
      }
    }
    // dodge tuck: knees to chest, back rounded, head down, arms hugging the shins
    if (oRoll > 0.001) {
      // the dive: body long and arms reaching for the first beat, then the tuck
      const rt = clamp(this.dodgeAge / ROLL_DUR, 0, 1);
      const dive = (1 - smooth(clamp((rt - 0.1) / 0.14, 0, 1))) * oRoll * (1 - oDeath);
      if (dive > 0.001) {
        this.rot(B.LeftArm, Y_AXIS, -1.2 * dive); this.rot(B.RightArm, Y_AXIS, 1.2 * dive);
        this.rot(B.LeftArm, Z_AXIS, 0.5 * dive); this.rot(B.RightArm, Z_AXIS, -0.5 * dive);
        this.rot(B.LeftUpLeg, X_AXIS, 0.35 * dive); this.rot(B.RightUpLeg, X_AXIS, 0.15 * dive);
        this.rot(B.Head, X_AXIS, -0.35 * dive);
      }
      const tk = oRoll * (1 - oDeath) * (1 - dive);
      this.rot(B.LeftUpLeg, X_AXIS, -1.5 * tk); this.rot(B.RightUpLeg, X_AXIS, -1.4 * tk);
      this.rot(B.LeftLeg, X_AXIS, 1.9 * tk); this.rot(B.RightLeg, X_AXIS, 1.8 * tk);
      this.rot(B.Spine, X_AXIS, 0.35 * tk); this.rot(B.Spine1, X_AXIS, 0.35 * tk); this.rot(B.Spine2, X_AXIS, 0.25 * tk);
      this.rot(B.Neck, X_AXIS, 0.35 * tk); this.rot(B.Head, X_AXIS, 0.3 * tk);
      this.rot(B.LeftArm, X_AXIS, -0.9 * tk); this.rot(B.RightArm, X_AXIS, -0.9 * tk);
      this.rot(B.LeftForeArm, Y_AXIS, 1.2 * tk); this.rot(B.RightForeArm, Y_AXIS, -1.2 * tk);
    }

    // ADS: chin down onto the stock, chest up, shoulders square (the arms are placed by IK below)
    if (this.adsW > 0.001) {
      const aw = this.adsW * aimW;
      this.rot(B.Head, X_AXIS, 0.16 * aw);
      this.rot(B.Neck, Y_AXIS, -0.1 * aw);
      this.rot(B.Spine2, X_AXIS, -0.05 * aw);
    }
    // the superhero landing: one knee down, the other foot planted forward, a fist on the ground, the
    // free arm thrown back, head down on impact then looking up as he rises
    if (oHero > 0.001) {
      const hw = oHero * (1 - oDeath);
      const look = smooth(clamp((this.heroAge - 0.18) / 0.2, 0, 1));
      this.rot(B.LeftUpLeg, X_AXIS, -1.25 * hw); this.rot(B.LeftLeg, X_AXIS, 1.45 * hw); this.rot(B.LeftFoot, X_AXIS, -0.45 * hw);
      this.rot(B.RightUpLeg, X_AXIS, -0.1 * hw); this.rot(B.RightLeg, X_AXIS, 1.55 * hw); this.rot(B.RightFoot, X_AXIS, 0.2 * hw);
      this.rot(B.RightUpLeg, Z_AXIS, -0.12 * hw);
      this.rot(B.Spine, X_AXIS, 0.38 * hw); this.rot(B.Spine1, X_AXIS, 0.22 * hw); this.rot(B.Spine2, X_AXIS, 0.08 * hw);
      this.rot(B.Neck, X_AXIS, (0.3 - 0.75 * look) * hw); this.rot(B.Head, X_AXIS, (0.25 - 0.6 * look) * hw);
      // right fist to the ground in front, left arm swept back and out
      this.rot(B.RightArm, Y_AXIS, 1.0 * hw); this.rot(B.RightForeArm, Y_AXIS, -0.15 * hw);
      this.rot(B.LeftArm, Z_AXIS, -0.55 * hw); this.rot(B.LeftArm, Y_AXIS, 0.9 * hw); this.rot(B.LeftForeArm, Y_AXIS, 0.35 * hw);
    }

    if (reach > 0.001) {
      this.rot(B.LeftUpLeg, X_AXIS, 0.55 * reach); this.rot(B.RightUpLeg, X_AXIS, 0.4 * reach);
      this.rot(B.LeftLeg, X_AXIS, -0.95 * reach); this.rot(B.RightLeg, X_AXIS, -0.75 * reach);
    }
    // rising: the push leg stays long and trails, the other knee drives up
    if (rise > 0.001) {
      this.rot(B.RightUpLeg, X_AXIS, 0.45 * rise); this.rot(B.RightLeg, X_AXIS, -0.6 * rise);
      this.rot(B.Spine1, X_AXIS, -0.12 * rise);
    }
    // turn in place: the spine carries what the feet haven't turned yet; the feet step while the root turns
    {
      const gap = wrapPi(c.yaw - this.bodyYaw) * wG * (1 - this.freeRun) * base;
      if (Math.abs(gap) > 0.002) { this.rot(B.Spine, Y_AXIS, gap * 0.3); this.rot(B.Spine1, Y_AXIS, gap * 0.3); this.rot(B.Spine2, Y_AXIS, gap * 0.25); }
      this.stepL = 0; this.stepR = 0;
      if (this.turnT > 0) {
        const ph = Math.sin(this.turnT * Math.PI * 2 / 0.36), sw = Math.min(1, this.turnT * 8) * wG * base;
        const l = Math.max(0, ph) * sw, r = Math.max(0, -ph) * sw;
        this.stepL = l; this.stepR = r;
        this.rot(B.LeftUpLeg, X_AXIS, -0.35 * l); this.rot(B.LeftLeg, X_AXIS, 0.6 * l);
        this.rot(B.RightUpLeg, X_AXIS, -0.35 * r); this.rot(B.RightLeg, X_AXIS, 0.6 * r);
      }
    }
    // recoil kick through the chest
    // (shooting feel) scaled per gun: a Scoundrel throws the arm up, a Pulse Rifle barely moves it
    const fb = c.weapon !== undefined && c.weapon >= 0 ? feelOf(c.weapon).body : 1;
    const kick = alive ? Math.exp(-c.shotAge * T.recoil.decay / Math.sqrt(fb)) * T.recoil.amp * aimW * fb : 0;
    if (kick > 0.002) {
      this.rot(B.Spine2, X_AXIS, -kick * 0.25);
      this.rot(B.RightShoulder, X_AXIS, -kick * 0.3);
      this.rot(B.RightForeArm, X_AXIS, -kick * 0.35);
      this.rot(B.Head, X_AXIS, kick * 0.12);
    }

    // traversal flight: the delta wing (FLIGHT_DESIGN §0.5b) built from the T-pose
    let pitchBody = 0, rollLong = 0, leanSide = 0;
    if (wF > 0.001) {
      const wg = T.wing;
      const f = clamp(speed / 24, 0.3, 1.6);
      const fq = time * (6 + 4 * f) * (this.cls === 1 ? 1.5 : this.cls === 3 ? 0.6 : 1);
      const sw = 0.05 * f * wg.sway;
      const lead = this.armLag;
      // a clean skydive / Iron-Man line: arms straight back along the flanks, palms trailing, a few degrees out
      // from the body; legs straight and together; only small sways so the silhouette stays one long dart
      const down = wF * (Math.PI / 2 - 0.2 + (1.15 - wg.out) * 0.15);
      this.rot(B.LeftArm, Z_AXIS, -down + wF * (lead * 0.6 + sw * 0.5 * Math.sin(fq + 0.5)));
      this.rot(B.RightArm, Z_AXIS, down + wF * (lead * 0.6 - sw * 0.5 * Math.sin(fq + 2.1)));
      this.rot(B.LeftArm, X_AXIS, wF * (0.18 + sw * 0.4 * Math.sin(fq * 1.3)));
      this.rot(B.RightArm, X_AXIS, wF * (0.18 + sw * 0.4 * Math.sin(fq * 1.3 + 1)));
      this.rot(B.LeftUpLeg, Z_AXIS, wF * 0.03); this.rot(B.RightUpLeg, Z_AXIS, -wF * 0.03);
      this.rot(B.LeftUpLeg, X_AXIS, wF * (0.04 + sw * 0.4 * Math.sin(fq * 0.8)));
      this.rot(B.RightUpLeg, X_AXIS, wF * (0.04 + sw * 0.4 * Math.sin(fq * 0.8 + 2.4)));
      this.rot(B.LeftLeg, X_AXIS, wF * (0.1 + sw * 0.6 * (1 + Math.sin(fq * 0.8 + 0.9))));
      this.rot(B.RightLeg, X_AXIS, wF * (0.06 + sw * 0.6 * (1 + Math.sin(fq * 0.8 + 3.3))));
      this.rot(B.LeftFoot, X_AXIS, wF * 0.8); this.rot(B.RightFoot, X_AXIS, wF * 0.8);
      // chest up, head up: looking where you're going
      this.rot(B.Spine1, X_AXIS, -wF * 0.12);
      this.rot(B.Spine2, Z_AXIS, wF * lead * 0.3);
      this.rot(B.Neck, X_AXIS, -wF * 0.5);
      this.rot(B.Head, X_AXIS, -wF * 0.55);
      pitchBody += this.flightPitch * wF;
      rollLong += this.bank;
      // class silhouettes on the shared dart, readable at range: Valkyrie swept wings, Phantom one fist out front
      // (the superhero line), Juggernaut elbows bent and fists forward with the legs apart (the heavy brick)
      if (this.cls === 1) { this.rot(B.LeftArm, Z_AXIS, wF * 0.45); this.rot(B.RightArm, Z_AXIS, -wF * 0.45); }
      else if (this.cls === 2) { this.rot(B.RightArm, Z_AXIS, -wF * (down / wF + 1.45)); this.rot(B.RightForeArm, Y_AXIS, -wF * 0.1); }
      else if (this.cls === 3) {
        this.rot(B.LeftArm, Z_AXIS, wF * 0.25); this.rot(B.RightArm, Z_AXIS, -wF * 0.25);
        this.rot(B.LeftForeArm, Y_AXIS, wF * 0.7); this.rot(B.RightForeArm, Y_AXIS, -wF * 0.7);
        this.rot(B.LeftUpLeg, Z_AXIS, -wF * 0.09); this.rot(B.RightUpLeg, Z_AXIS, wF * 0.09);
      }
    }
    // hover: legs dangle, toes down, a slow kick
    const wDang = wH + wS * 0.6;
    if (wDang > 0.001) {
      // a slow treading kick: the legs swing out of phase and visibly trade places over ~2.5 s
      // a 2 s period: one leg drawn up (knee bent hard), the other hanging long, trading places every second,
      // so any two frames a second apart are mirror poses
      const s1 = Math.sin(time * Math.PI + this.id * 1.3), s2 = -s1;
      this.rot(B.LeftUpLeg, X_AXIS, -wDang * (0.3 + 0.5 * s1));
      this.rot(B.RightUpLeg, X_AXIS, -wDang * (0.3 + 0.5 * s2));
      this.rot(B.LeftUpLeg, Z_AXIS, -wDang * (0.06 + 0.06 * s2));
      this.rot(B.RightUpLeg, Z_AXIS, wDang * (0.06 + 0.06 * s1));
      this.rot(B.LeftLeg, X_AXIS, wDang * (0.25 + 1.0 * Math.max(0, s1)));
      this.rot(B.RightLeg, X_AXIS, wDang * (0.25 + 1.0 * Math.max(0, s2)));
      this.rot(B.LeftFoot, X_AXIS, wDang * 0.5); this.rot(B.RightFoot, X_AXIS, wDang * 0.45);
    }
    // pause lift: arms out, legs hang, face the sky
    if (wS > 0.001) {
      const s1 = Math.sin(time * 1.4 + this.id);
      this.rot(B.LeftArm, Z_AXIS, wS * (0.55 + 0.06 * s1));
      this.rot(B.RightArm, Z_AXIS, -wS * (0.55 + 0.06 * s1));
      this.rot(B.Head, X_AXIS, -0.35 * wS);
    }
    // ---- saloon scripted poses (client/saloon/director.ts sets c.scripted) -- BEGIN saloon lane
    // 1 sat at the bar, 2 sat eating (the hotdog scene), 4 braced for the roof launch. 3 (superhero
    // landing) belongs to the movement lane. Procedural on top of the idle so it needs no new clips.
    // once the movement lane's re-bake (bar_sit / bar_eat clips) is in, it poses 1 and 2 itself
    if (((c.scripted === 1 || c.scripted === 2) && !rig.clip.bar_sit) || c.scripted === 4) {
      const st = c.scriptedT ?? 0;
      const k = smooth(clamp(st / 0.35, 0, 1));
      if (c.scripted !== 4) {
        // the director drops the root ~0.85 m under the stool top, so the hips stay near standing height;
        // thighs level, shins hanging, a hunch to the bar
        _v.fromArray(a.bones[B.Hips].pos);
        hipsB.position.y += (_v.y * 0.88 - hipsB.position.y) * k;
        this.rot(B.LeftUpLeg, X_AXIS, -1.45 * k); this.rot(B.RightUpLeg, X_AXIS, -1.4 * k);
        this.rot(B.LeftUpLeg, Z_AXIS, -0.08 * k); this.rot(B.RightUpLeg, Z_AXIS, 0.1 * k);
        this.rot(B.LeftLeg, X_AXIS, 1.35 * k); this.rot(B.RightLeg, X_AXIS, 1.2 * k);
        this.rot(B.Spine, X_AXIS, 0.12 * k); this.rot(B.Spine1, X_AXIS, 0.1 * k);
        // forearms on the bar
        this.rot(B.LeftArm, X_AXIS, -0.75 * k); this.rot(B.RightArm, X_AXIS, -0.75 * k);
        this.rot(B.LeftArm, Z_AXIS, -0.35 * k); this.rot(B.RightArm, Z_AXIS, 0.35 * k);
        this.rot(B.LeftForeArm, Y_AXIS, 0.9 * k); this.rot(B.RightForeArm, Y_AXIS, -0.9 * k);
        if (c.scripted === 2) {
          // a bite every ~2.4 s: the right hand comes up to the mouth and the head dips to meet it
          const b = Math.max(0, Math.sin(st * 2.6 - 0.8));
          const bite = b * b * k;
          this.rot(B.RightArm, X_AXIS, -0.5 * bite);
          this.rot(B.RightForeArm, Y_AXIS, -1.0 * bite);
          this.rot(B.Head, X_AXIS, 0.18 * bite);
        }
      } else {
        // launch brace: knees bent, weight down, fists low, chin up at the skylight
        const tremble = Math.sin(time * 41 + this.id) * 0.015;
        _v.fromArray(a.bones[B.Hips].pos);
        hipsB.position.y += (_v.y * 0.82 - hipsB.position.y) * k;
        this.rot(B.LeftUpLeg, X_AXIS, -0.65 * k); this.rot(B.RightUpLeg, X_AXIS, -0.55 * k);
        this.rot(B.LeftLeg, X_AXIS, 1.05 * k); this.rot(B.RightLeg, X_AXIS, 0.95 * k);
        this.rot(B.LeftFoot, X_AXIS, -0.4 * k); this.rot(B.RightFoot, X_AXIS, -0.4 * k);
        this.rot(B.Spine, X_AXIS, (0.3 + tremble) * k);
        this.rot(B.LeftArm, Z_AXIS, -0.35 * k); this.rot(B.RightArm, Z_AXIS, 0.35 * k);
        this.rot(B.Neck, X_AXIS, -0.3 * k); this.rot(B.Head, X_AXIS, -0.35 * k);
      }
    }
    // ---- END saloon lane
    // hover: the thrust isn't perfectly steady -> a bob, a slow pitch rock and a side sway
    let bobY = 0;
    // landing compression: the hips give a few cm and spring back with a little overshoot
    if (this.landAge < 0.35 && c.grounded) { const u = this.landAge / 0.35; bobY -= 0.06 * this.landW * Math.sin(Math.PI * Math.min(1, u * 1.6)) * (1 - u) * wG; }
    if (wH > 0.001) {
      // same 2 s beat as the legs: high on one second, low on the next; the torso sways on the half beat
      bobY = wH * 0.15 * Math.sin(time * Math.PI + this.id * 1.3 + 0.6);
      pitchBody += wH * 0.14 * Math.sin(time * Math.PI * 0.5 + this.id);
      leanSide += wH * 0.16 * Math.sin(time * Math.PI * 0.5 + this.id * 2.3 + 0.9);
      this.rot(B.Spine1, Z_AXIS, wH * 0.14 * Math.sin(time * Math.PI * 0.5 + this.id * 2.3 + 1.6));
    }
    // hover / air lean into the drift
    pitchBody += clamp(-fwdS * 0.02, -0.25, 0.25) * wH + clamp(-fwdS * 0.012, -0.15, 0.15) * wA;
    leanSide += clamp(-rightS * 0.03, -0.3, 0.3) * wH - 0.012 * rightS * wG * this.runAmt;
    // a curving run banks into the turn (the centripetal lean, ~12 deg at a full-speed weave), like a runner on a bend
    leanSide += clamp(this.yawRate * h * 0.02, -0.3, 0.3) * wG * this.runAmt * base;
    // specials: dash lean (Phantom warp stretches and flickers)
    let stretch = 0;
    const sDur = this.cls === 2 ? 0.2 : this.cls === 1 ? 0.3 : 0.5;
    if (this.specialAge < sDur + 0.08) {
      const t = clamp(this.specialAge / sDur, 0, 1);
      const env = Math.sin(Math.PI * Math.min(1, t * 1.1));
      if (this.cls === 2) { stretch = env; } // the Phantom warps: no lean, no roll; the body strobes (flick) through it
      else if (this.cls === 1) pitchBody += (c.grounded ? -0.5 : -Math.PI / 2 + 0.2 + clamp(c.pitch, -1, 1)) * env;
      else if (this.cls === 3) { pitchBody -= 0.4 * env; this.rot(B.Spine1, Y_AXIS, -0.4 * env); }
      else pitchBody -= 0.45 * env;
    }
    // air dodge: barrel roll about the travel axis
    let dodgeAng = 0;
    // ground dodge: a real shoulder roll, a full somersault about the axis across the travel direction,
    // tucked low (the roll clip supplies the tuck), so from behind it reads as a roll and not a run
    let rollDrop = 0;
    if (oRoll > 0.001 && !this.dodgeAir) {
      // dive forward (~60 deg of the turn while the body is long and lifted), then the tucked somersault
      const rt = clamp(this.dodgeAge / ROLL_DUR, 0, 1);
      const diveA = 1.05;
      // playtest 2 (Nick): the turn was crammed into half the roll and read as missing frames; it now runs over 0.7 of it
      dodgeAng = rt < 0.14 ? diveA * smooth(rt / 0.14) : diveA + (TAU - diveA) * smooth(clamp((rt - 0.14) / 0.72, 0, 1));
      rollDrop = rt < 0.14 ? 0.08 * Math.sin(Math.PI * rt / 0.14) : -0.42 * Math.sin(Math.PI * clamp((rt - 0.14) / 0.76, 0, 1));
      if (this.dodgeAge < dt * 1.5 || this.rollAxisFresh) {
        let dx = rightS, dz = -fwdS;
        const l = Math.hypot(dx, dz);
        if (l < 0.5) { dx = 0; dz = 1; } else { dx /= l; dz /= l; }
        this.dodgeAxis.set(dz, 0, -dx);
        this.rollAxisFresh = false;
      }
    } else this.rollAxisFresh = true;
    if (dodging && this.dodgeAir) {
      const t = clamp(this.dodgeAge / this.dodgeDur, 0, 1);
      const e = t * t * t * (t * (t * 6 - 15) + 10);
      const env = Math.sin(Math.PI * t);
      if (this.dodgeSpin === 2) {
        // the barrel roll: one full turn about the line of flight, toward the juke (the back rolls over that way),
        // balled up through the middle of it so it reads as a snap roll and not a spit-roast
        dodgeAng = e * TAU * this.barrelSign;
        this.dodgeAxis.set(0, 0, -1);
        this.rot(B.LeftUpLeg, X_AXIS, -0.75 * env); this.rot(B.RightUpLeg, X_AXIS, -0.75 * env);
        this.rot(B.LeftLeg, X_AXIS, 1.3 * env); this.rot(B.RightLeg, X_AXIS, 1.3 * env);
        this.rot(B.LeftArm, X_AXIS, -0.7 * env); this.rot(B.RightArm, X_AXIS, -0.7 * env);
        this.rot(B.LeftForeArm, Y_AXIS, 0.9 * env); this.rot(B.RightForeArm, Y_AXIS, -0.9 * env);
        this.rot(B.Head, X_AXIS, 0.35 * env);
      } else if (this.dodgeSpin === 3) {
        // the hover dodge: a thrust flip with the head leading into it. Sideways is a cartwheel, forward a front
        // flip, back a backflip. Tucked tight so the turn is quick and the guns come round on target
        if (this.dodgeAge < 0.07) {
          // the axis is up x the dodge direction (body frame: right +x, forward -z), so rotating by + tips the head into d
          const l = Math.hypot(rightS, fwdS);
          if (l > 1.5 || this.dodgeAge < dt * 1.5) {
            let dx = rightS, dz = -fwdS;
            if (l < 0.5) { dx = 0; dz = 1; } else { dx /= l; dz /= l; }
            this.dodgeAxis.set(dz, 0, -dx);
          }
        }
        dodgeAng = e * TAU;
        this.rot(B.LeftUpLeg, X_AXIS, -1.3 * env); this.rot(B.RightUpLeg, X_AXIS, -1.3 * env);
        this.rot(B.LeftLeg, X_AXIS, 1.7 * env); this.rot(B.RightLeg, X_AXIS, 1.7 * env);
        this.rot(B.LeftArm, X_AXIS, -0.7 * env); this.rot(B.RightArm, X_AXIS, -0.7 * env);
        this.rot(B.LeftForeArm, Y_AXIS, 0.9 * env); this.rot(B.RightForeArm, Y_AXIS, -0.9 * env);
        this.rot(B.Spine1, X_AXIS, 0.3 * env); this.rot(B.Head, X_AXIS, 0.3 * env);
        rollDrop -= 0.25 * env;
      } else if (this.dodgeSpin === 1) {
        // the air dive: a belly-flop. The body goes long and flat, nose down a touch, arms out ahead to break the
        // fall, legs trailing; it holds until touchdown, when the prone pose takes it
        const dv = smooth(clamp(this.dodgeAge / 0.16, 0, 1));
        pitchBody += -(Math.PI / 2 - 0.25) * dv;
        this.rot(B.LeftArm, Z_AXIS, -1.2 * dv); this.rot(B.RightArm, Z_AXIS, 1.2 * dv);
        this.rot(B.LeftArm, X_AXIS, -0.9 * dv); this.rot(B.RightArm, X_AXIS, -0.9 * dv);
        this.rot(B.LeftForeArm, X_AXIS, -0.25 * dv); this.rot(B.RightForeArm, X_AXIS, -0.25 * dv);
        this.rot(B.LeftUpLeg, X_AXIS, 0.25 * dv); this.rot(B.RightUpLeg, X_AXIS, 0.35 * dv);
        this.rot(B.LeftLeg, X_AXIS, 0.3 * dv); this.rot(B.RightLeg, X_AXIS, 0.2 * dv);
        this.rot(B.Neck, X_AXIS, -0.45 * dv); this.rot(B.Head, X_AXIS, -0.45 * dv);
        this.rot(B.Spine1, X_AXIS, -0.15 * dv);
      } else {
        dodgeAng = e * TAU;
        if (this.dodgeAge < dt * 1.5) {
          let lx = rightS, ly = c.vy, lz = -fwdS;
          let l = Math.hypot(lx, ly, lz);
          if (l < 0.5) { lx = 0; ly = 0; lz = 1; l = 1; }
          this.dodgeAxis.set(lx / l, ly / l, lz / l);
          if (wF > 0.5) this.dodgeAxis.set(0, 0, -1);
        }
      }
    }
    // the launch: a crouch-and-punch off the pack. Knees snap up and the chin tucks for an instant while the
    // burner lights, then the body unfolds long into the flight dart (wF carries it the rest of the way)
    if (this.launchAge < LAUNCH_DUR && alive) {
      const t = this.launchAge / LAUNCH_DUR;
      const env = t < 0.3 ? smooth(t / 0.3) : 1 - smooth((t - 0.3) / 0.7);
      this.rot(B.LeftUpLeg, X_AXIS, -0.9 * env); this.rot(B.RightUpLeg, X_AXIS, -0.7 * env);
      this.rot(B.LeftLeg, X_AXIS, 1.5 * env); this.rot(B.RightLeg, X_AXIS, 1.3 * env);
      this.rot(B.Spine1, X_AXIS, 0.25 * env); this.rot(B.Head, X_AXIS, 0.25 * env);
      this.rot(B.LeftArm, Z_AXIS, -0.25 * env); this.rot(B.RightArm, Z_AXIS, 0.25 * env);
      pitchBody += 0.25 * env; // coil upright, then the punch lays him out long
      // the trailing legs straighten out of the coil into the dart
      const tr = wF * (t > 0.3 ? smooth((t - 0.3) / 0.7) : 0) * (1 - t);
      this.rot(B.LeftLeg, X_AXIS, -0.3 * tr); this.rot(B.RightLeg, X_AXIS, -0.3 * tr);
    }

    // the stall: the airframe bites, nose up against the travel; sprint leans into the run
    const stl = this.stallW * Math.min(1, wH + wF);
    pitchBody += 0.9 * stl;
    if (stl > 0.001) {
      // the flare: arms flung forward and out, legs swinging through under the body
      this.rot(B.LeftArm, X_AXIS, -0.6 * stl); this.rot(B.RightArm, X_AXIS, -0.6 * stl);
      this.rot(B.LeftArm, Z_AXIS, 0.3 * stl); this.rot(B.RightArm, Z_AXIS, -0.3 * stl);
      this.rot(B.LeftUpLeg, X_AXIS, -0.5 * stl); this.rot(B.RightUpLeg, X_AXIS, -0.35 * stl);
      this.rot(B.LeftLeg, X_AXIS, 0.4 * stl); this.rot(B.RightLeg, X_AXIS, 0.6 * stl);
    }
    // the sprint lean lives in the spine, so the planted feet stay flat on the ground
    const spl = this.sprintW * wG * this.runAmt;
    if (spl > 0.001) { this.rot(B.Spine, X_AXIS, 0.16 * spl); this.rot(B.Spine1, X_AXIS, 0.12 * spl); this.rot(B.Neck, X_AXIS, -0.12 * spl); }
    if (oHero > 0.001) rollDrop -= 0.4 * oHero;

    // ---- movement lane: prone after the dive (belly down, sliding, then a push-up get-up), and the slide
    if (oProne > 0.001) {
      const pa = this.proneAge;
      // the get-up is the last PRONE_GETUP of the prone (the sim's clock; we don't know the slide's length, so
      // the pose rises as the weight fades out instead)
      const flat = oProne;
      pitchBody += -(Math.PI / 2 - 0.12) * flat;
      rollDrop -= (a.height * 0.5 - 0.2) * flat;
      this.rot(B.LeftArm, Z_AXIS, -1.0 * flat); this.rot(B.RightArm, Z_AXIS, 1.0 * flat);
      this.rot(B.LeftArm, X_AXIS, -1.1 * flat); this.rot(B.RightArm, X_AXIS, -1.1 * flat);
      this.rot(B.LeftForeArm, X_AXIS, -0.6 * flat); this.rot(B.RightForeArm, X_AXIS, -0.6 * flat);
      this.rot(B.LeftUpLeg, X_AXIS, 0.15 * flat); this.rot(B.RightUpLeg, X_AXIS, 0.1 * flat);
      this.rot(B.LeftUpLeg, Z_AXIS, 0.12 * flat); this.rot(B.RightUpLeg, Z_AXIS, -0.12 * flat);
      this.rot(B.Neck, X_AXIS, -0.6 * flat); this.rot(B.Head, X_AXIS, -0.5 * flat);
      // the landing thump, and a little bounce of the chest as the slide bleeds off
      if (pa < 0.2) rollDrop -= 0.06 * Math.sin(Math.PI * pa / 0.2) * flat;
      this.rot(B.Spine1, X_AXIS, 0.12 * Math.sin(pa * 9) * Math.exp(-pa * 3) * flat);
    }
    if (oSlide > 0.001) {
      // the slide: leaning back on one hip, the lead leg out straight, the trailing knee bent, an arm back on the ground
      const sl = oSlide, sd = (this.id % 2) ? 1 : -1;
      pitchBody += 0.55 * sl;
      leanSide += 0.25 * sd * sl;
      rollDrop -= 0.42 * sl;
      this.rot(B.LeftUpLeg, X_AXIS, -1.2 * sl); this.rot(B.RightUpLeg, X_AXIS, -0.6 * sl);
      this.rot(B.LeftLeg, X_AXIS, 0.2 * sl); this.rot(B.RightLeg, X_AXIS, 1.5 * sl);
      this.rot(B.Spine, X_AXIS, 0.25 * sl); this.rot(B.Spine1, X_AXIS, 0.1 * sl);
      this.rot(B.LeftArm, X_AXIS, 0.9 * sl); this.rot(B.LeftArm, Z_AXIS, -0.5 * sl);
      this.rot(B.Head, X_AXIS, -0.4 * sl);
    }

    // ---- weight lane: the flinch. A hit that hurt snaps the body away from where it came from (back if it hit the
    // front, a sideways lurch if it hit the flank), the shoulders throw, the head whips, and it springs back.
    let knockOX = 0, knockOY = 0, knockOZ = 0;
    // caught in a Time grenade (playtest 2): the wiggle blur, a fast shiver through the spine and the head
    if (c.slowed && alive) { const w = Math.sin(time * 151 + this.id) * 0.06, w2 = Math.cos(time * 127 + this.id * 2) * 0.05; this.rot(B.Spine1, Z_AXIS, w); this.rot(B.Spine2, X_AXIS, w2); this.rot(B.Head, Z_AXIS, -w * 1.5); this.rot(B.LeftArm, X_AXIS, w2); this.rot(B.RightArm, X_AXIS, -w2); }
    const ha = c.hurtAge ?? 99;
    if (ha < 0.4 && alive && oKnock < 0.5) {
      const pow = Math.min(1.2, c.hurtPow ?? 0.3);
      const env = Math.exp(-ha * 8) * Math.cos(ha * 20) * (0.55 + 0.85 * pow); // playtest 2: a hit is felt in the whole body
      const dx = c.hurtDX ?? 0, dz = c.hurtDZ ?? 1;
      const r = dx * Math.cos(c.yaw) - dz * Math.sin(c.yaw), fw = -dx * Math.sin(c.yaw) - dz * Math.cos(c.yaw);
      pitchBody += -fw * 0.22 * env;
      leanSide -= r * 0.18 * env;
      this.rot(B.Spine1, X_AXIS, -fw * 0.25 * env); this.rot(B.Spine2, Z_AXIS, r * 0.2 * env);
      this.rot(B.Neck, X_AXIS, -fw * 0.3 * env); this.rot(B.Head, X_AXIS, -fw * 0.35 * env); this.rot(B.Head, Z_AXIS, r * 0.3 * env);
      this.rot(B.LeftArm, Z_AXIS, 0.35 * env); this.rot(B.RightArm, Z_AXIS, -0.35 * env);
      this.rot(B.LeftArm, X_AXIS, fw * 0.3 * env); this.rot(B.RightArm, X_AXIS, fw * 0.3 * env);
    }
    // ---- weight lane: the knockdown and the ragdoll fling
    if (oKnock > 0.001) {
      const r = this.knockR, fw = this.knockF, sd = this.knockSeed;
      const flat = Math.max(kFall, kLie) * (1 - kUp) * oKnock;
      // tipped over along the shove: back-shoved lands on the back, side-shoved on the flank
      pitchBody += -fw * 1.45 * flat;
      leanSide -= r * 1.45 * flat;
      // the body's centre drops to the ground as it goes flat
      rollDrop -= (a.height * 0.5 - 0.22) * flat;
      // the fling: a thrown arc along the shove with a tumble, limbs trailing loose; then the landing's bounce
      if (kFly > 0.001) {
        const sp = Math.min(22, c.knockSpeed ?? 14);
        const t = ka / KNOCK_FLING;
        const dist = sp * 0.22 * (1 - Math.pow(1 - t, 2.2));
        const dx = c.knockDX ?? 0, dz = c.knockDZ ?? 1;
        knockOX = dx * dist * kFly; knockOZ = dz * dist * kFly;
        knockOY = Math.max(0, Math.sin(Math.PI * Math.min(1, t * 1.1)) * (0.5 + sp * 0.03)) * kFly;
        const spin = (0.4 + 0.5 * sd) * Math.PI * 2 * t * kFly;
        pitchBody += -fw * spin; leanSide -= r * spin;
        const loose = kFly;
        this.rot(B.LeftArm, Z_AXIS, (0.9 + 0.6 * sd) * loose); this.rot(B.RightArm, Z_AXIS, -(1.1 - 0.5 * sd) * loose);
        this.rot(B.LeftArm, X_AXIS, -(0.6 + 0.8 * sd) * loose); this.rot(B.RightArm, X_AXIS, -(1.2 - 0.8 * sd) * loose);
        this.rot(B.LeftForeArm, Y_AXIS, 0.9 * loose); this.rot(B.RightForeArm, Y_AXIS, -1.1 * loose);
        this.rot(B.LeftUpLeg, X_AXIS, -(0.5 + 0.6 * sd) * loose); this.rot(B.RightUpLeg, X_AXIS, (0.3 - 0.9 * sd) * loose);
        this.rot(B.LeftLeg, X_AXIS, (0.9 + 0.5 * sd) * loose); this.rot(B.RightLeg, X_AXIS, (0.4 + 0.9 * sd) * loose);
        this.rot(B.Neck, X_AXIS, 0.45 * loose); this.rot(B.Head, Z_AXIS, (sd - 0.5) * 0.8 * loose);
      }
      // lying: splayed, one knee up, an arm out, the head rolled to the side; the get-up gathers it all back
      const lie = flat;
      if (lie > 0.001) {
        this.rot(B.LeftArm, Z_AXIS, (0.7 + 0.5 * sd) * lie); this.rot(B.RightArm, Z_AXIS, -(1.2 - 0.6 * sd) * lie);
        this.rot(B.LeftForeArm, Y_AXIS, 0.6 * lie); this.rot(B.RightForeArm, Y_AXIS, -0.4 * lie);
        this.rot(B.LeftUpLeg, X_AXIS, -(0.9 * sd) * lie); this.rot(B.RightUpLeg, X_AXIS, -(0.2 + 0.4 * (1 - sd)) * lie);
        this.rot(B.LeftLeg, X_AXIS, (1.3 * sd) * lie); this.rot(B.RightLeg, X_AXIS, 0.5 * lie);
        this.rot(B.Head, Z_AXIS, (sd - 0.5) * 0.9 * lie); this.rot(B.Head, X_AXIS, 0.2 * lie);
      }
      // the landing's thump: a squash into the ground as it hits
      const tf = ka - kFling;
      if (tf > 0 && tf < 0.25) rollDrop -= 0.08 * Math.sin(Math.PI * tf / 0.25) * oKnock;
      // the get-up: a hand pushes off, the knee comes under, the body folds up through a crouch
      if (kUp > 0.001) {
        const u = kUp * (1 - kUp) * 4 * oKnock;
        this.rot(B.RightArm, X_AXIS, 0.8 * u); this.rot(B.RightForeArm, X_AXIS, -0.6 * u);
        this.rot(B.Spine, X_AXIS, 0.5 * u); this.rot(B.Spine1, X_AXIS, 0.3 * u);
        this.rot(B.LeftUpLeg, X_AXIS, -1.2 * u); this.rot(B.LeftLeg, X_AXIS, 1.6 * u);
      }
    }

    // ---- root: position, yaw, tumble
    this.group.position.set(c.x + knockOX, c.y + bobY + knockOY, c.z + knockOZ);
    this.group.rotation.set(0, this.bodyYaw, 0);
    const pivot = a.height * 0.5;
    _q.setFromAxisAngle(X_AXIS, pitchBody);
    _q2.setFromAxisAngle(Y_AXIS, rollLong); _q.multiply(_q2);
    _q2.setFromAxisAngle(Z_AXIS, leanSide); _q.multiply(_q2);
    if (dodgeAng !== 0) { _qa.setFromAxisAngle(this.dodgeAxis, dodgeAng); _q.premultiply(_qa); }
    this.tumble.quaternion.copy(_q);
    // pivot about the body centre: tumble rotates around (0, pivot, 0)
    _v.set(0, -pivot, 0).applyQuaternion(_q);
    this.tumble.position.set(_v.x, _v.y + pivot + rollDrop, _v.z);
    if (stretch > 0.001) this.tumble.scale.set(1 - 0.35 * stretch, 1 - 0.15 * stretch, 1 + 1.4 * stretch);
    else this.tumble.scale.set(1, 1, 1);
    // the gun bone stays at identity while the hierarchy updates, then is placed in model space
    this.gun.position.set(0, 0, 0); this.gun.quaternion.identity();
    this.group.updateMatrixWorld(true);
    // ground roll contact: through the tuck the ball rolls ON the ground (lowest bone ~4 cm up) instead of
    // cartwheeling half a metre above it; the somersault pivot alone can't know where the tucked body ends
    if (oRoll > 0.001 && !this.dodgeAir) {
      const rt = clamp(this.dodgeAge / ROLL_DUR, 0, 1);
      const cw = smooth(clamp((rt - 0.1) / 0.1, 0, 1)) * (1 - smooth(clamp((rt - 0.72) / 0.14, 0, 1))) * oRoll * (1 - oDeath);
      if (cw > 0.001) {
        let mn = 1e9;
        for (let i = 0; i < nB; i++) { if (rig.fingers[i]) continue; Bn[i].getWorldPosition(_v); if (_v.y < mn) mn = _v.y; }
        this.tumble.position.y += (c.y + 0.04 - mn) * cw;
        this.group.updateMatrixWorld(true);
      }
    }
    // ---- planted feet (feet.ts): on the ground with nothing else driving the legs, a foot that comes down stays where
    // it landed in the world until the clip lifts it, and both feet stand on the ground under them (a downed crawl too)
    {
      const legsFree = c.grounded && (alive || c.phase === 1) && !dashing && !(T.mFull && oMelee > 0.05) && c.skyT < 0.001
        && Math.sqrt(camPos.distanceToSquared(this.group.position)) < 60;
      const on = legsFree && !(globalThis as { __noFeet?: boolean }).__noFeet ? (wG + (this.rig!.clip.cwalk_f ? wD : 0)) * base * (1 - this.bodyW) : 0; // __noFeet: probes compare against the bare clips
      // contact per foot: the stride's own footfalls as soon as it is really striding; standing, both feet are down
      // unless a turn-in-place step has one up
      const iw = this.cW > 1e-4 ? 1 / this.cW : 0, mv = this.cW > 1e-4 ? smooth(clamp(this.runAmt * 4, 0, 1)) : 0;
      const sL = this.stepL > 0.12 ? 0 : 1, sR = this.stepR > 0.12 ? 0 : 1;
      // a strafe walks in two lanes: the Mixamo side-steps cross the feet, which reads as tripping from behind
      const lanes = smooth(clamp(Math.abs(Math.sin(this.dirAng)) * 1.6, 0, 1)) * smooth(clamp(this.runAmt * 2, 0, 1)) * (1 - this.sprintW);
      if (this.feet.solve(dt, on, this.runAmt > 0.2 ? 1 / this.strideHz : 1, lanes, sL + (this.cL * iw - sL) * mv, sR + (this.cR * iw - sR) * mv, c.y, -sy, -cy,
        Bn[B.Hips], [Bn[B.LeftUpLeg], Bn[B.RightUpLeg]], [Bn[B.LeftLeg], Bn[B.RightLeg]], [Bn[B.LeftFoot], Bn[B.RightFoot]], ik2, setWorldQuat)) {
        // the hips may have dropped: the rest of the body (arms, gun, pack) reads this frame's matrices
        this.group.updateMatrixWorld(true);
      }
    }
    // ---- playtest 1 (Nick): the Phantom's warp on screen. A double image: a ghost of the body stays where he warped
    // from and fades; a yellow hyper-shield wrap hugs him through the warp's i-frames (Godot's warp look)
    if (this.body && (this.cls === 2 || (c.hurtShield && (c.hurtAge ?? 99) < 0.5))) this.body.updateWorldMatrix(true, false); // the ghost and the shell need this frame's body matrix, not last frame's
    if (this.cls === 2) {
      const wa = c.warpAge ?? 99;
      const dashStart = dashing && !this.dashGhostDone;
      if (dashStart) { this.dashGhostDone = true; this.ghostWorld.copy(this.lastPos); }
      if (!dashing) this.dashGhostDone = false;
      if ((wa < this.lastWarpAge && wa < 0.25) || dashStart) {
        // a fresh warp (or a dash): the ghost stays where the body left from
        if (!dashStart) this.ghostWorld.copy(this.lastPos);
        this.ghostOffset.copy(this.ghostWorld).sub(this.group.position);
        if ((this.ghostOffset.lengthSq() > 1 || dashStart) && this.body) {
          if (!this.ghost) {
            this.ghostMat = new THREE.MeshBasicMaterial({ color: 0xffd34a, transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, depthWrite: false });
            const g = new THREE.SkinnedMesh(this.body.geometry, this.ghostMat);
            g.bindMode = 'detached'; g.frustumCulled = false; g.matrixAutoUpdate = false; g.matrixWorldAutoUpdate = false;
            g.bind(this.body.skeleton, this.body.bindMatrix);
            this.body.parent!.add(g);
            this.ghost = g;
          }
          this.ghostAge = 0;
          // round 2 (James): the warp's ghost is the Phantom's violet and hangs a beat longer (the fold FX play
          // around it, fx/classfx.ts); the ground dash keeps Nick's yellow
          this.ghostDur = dashStart ? 0.32 : 0.5;
          this.ghostMat!.color.setHex(dashStart ? 0xffd34a : 0xb98cff);
        }
      }
      this.lastWarpAge = wa;
      if (this.ghost && this.ghostAge < this.ghostDur && this.body) {
        this.ghostAge += dt;
        const k = 1 - this.ghostAge / this.ghostDur;
        this.ghostMat!.opacity = 0.55 * k;
        this.ghost.visible = true;
        this.ghostOffset.copy(this.ghostWorld).sub(this.group.position); // the ghost holds its ground while the dash runs on
        this.ghost.matrixWorld.copy(this.body.matrixWorld).premultiply(_m4.makeTranslation(this.ghostOffset.x, this.ghostOffset.y, this.ghostOffset.z));
        this.ghost.bindMatrixInverse.copy(this.body.matrixWorld).invert();
      } else if (this.ghost) this.ghost.visible = false;
    }
    // the wrap and the shield ring (playtest 2, Nick): drawn by the body's own material (cowboyMaterial uShell / uHit /
    // uRing), so they sit on the body by construction. Yellow and whole through the Phantom's warp / dash i-frames; a blue
    // ring running out from where a hit came in when it landed on the shield
    if (this.matSolid) {
      const u = this.matSolid.userData.u;
      const wa = c.warpAge ?? 99;
      const wrapOn = this.cls === 2 && (wa < 0.27 || dashing);
      const ha = c.hurtAge ?? 99;
      // the Dune shield skin (below) shows a blocked shot now; the body's own ring stays only where there is no skin
      const pulse = !this.shieldSkin && !!c.hurtShield && ha < 0.5;
      u.uShell.value = wrapOn ? 0.75 + 0.25 * Math.sin(time * 40) : 0;
      if (pulse) {
        // the ring starts where the hit came in (toward its source, at chest height) and runs out over the body
        u.uHit.value.set(this.group.position.x - (c.hurtDX ?? 0) * 0.35, this.group.position.y + a.height * 0.55, this.group.position.z - (c.hurtDZ ?? 1) * 0.35);
        u.uRing.value = ha * 4.5;
      } else u.uRing.value = -1;
    }
    // the Dune shield skins: the hit lights the skin from where it came in (the same point as Nick's ring)
    if (this.shieldSkin) {
      const st = this.shState, ha = c.hurtAge ?? 99;
      st.k = c.shieldK ?? 0; st.over = c.overK ?? 0;
      st.hitAge = c.hurtShield ? ha : 99;
      if (st.hitAge < 0.7) { st.hx = this.group.position.x - (c.hurtDX ?? 0) * 0.3; st.hy = this.group.position.y + a.height * 0.62; st.hz = this.group.position.z - (c.hurtDZ ?? 1) * 0.3; }
      st.brkAge = c.shieldBrkAge ?? 99; st.overBrkAge = c.overBrkAge ?? 99; st.regenAge = c.shieldRegenAge ?? 99; st.drainAge = c.shieldDrainAge ?? 99;
      st.cloaked = c.cloaked;
      this.shieldSkin.update(st, alive && this.group.visible && !c.hidden, time);
    }
    this.lastPos.copy(this.group.position);
    // the Phantom's dash: the whole body leans into the line, no tumble
    if (dashing && B.Spine >= 0) { const k = Math.min(1, this.dodgeAge * 12) * (1 - smooth(clamp((this.dodgeAge - 0.22) / 0.12, 0, 1))); this.rot(B.Spine, X_AXIS, 0.42 * k); if (B.Spine1 >= 0) this.rot(B.Spine1, X_AXIS, 0.2 * k); }
    // ADS: bring the gun hand up to the eye line (two-bone IK), so the weapon is shouldered and sighted
    const adsIk = this.adsW * aimW * (1 - oRoll) * (1 - oMelee) * (1 - oReload) * (1 - spinW);
    if (adsIk > 0.01 && B.RightArm >= 0 && B.RightForeArm >= 0 && B.RightHand >= 0 && B.Head >= 0) {
      const cp = Math.cos(c.pitch);
      _v.set(-Math.sin(c.yaw) * cp, Math.sin(c.pitch), -Math.cos(c.yaw) * cp); // aim forward
      _v2.set(Math.cos(c.yaw), 0, -Math.sin(c.yaw)); // aim right
      Bn[B.Head].getWorldPosition(_v3);
      const [k, rgt, dn] = hold.ads; // pistols at arm's length, long guns into the shoulder, tubes on it
      _ikT.copy(_v3).addScaledVector(_v, k).addScaledVector(_v2, rgt).add(_ikUp.set(0, -dn, 0));
      Bn[B.RightHand].getWorldPosition(_ikH);
      _ikT.lerp(_ikH, 1 - adsIk);
      Bn[B.RightArm].getWorldPosition(_ikP).addScaledVector(_v2, 0.35).add(_ikUp.set(0, -0.6, 0)).addScaledVector(_v, -0.2);
      ik2(Bn[B.RightArm], Bn[B.RightForeArm], Bn[B.RightHand], _ikT, _ikP);
    }

    // the swallow: head back, and the fist up in front of the face so the bottle stands tipped, neck at his lips
    const gw = oChug > 0.001 ? chugGulpW(da / CHUG_DUR) * oChug : 0;
    if (gw > 0.001 && B.Head >= 0 && B.Neck >= 0 && B.RightArm >= 0 && B.RightForeArm >= 0 && B.RightHand >= 0) {
      const yaw = this.bodyYaw;
      _v2.set(Math.cos(yaw), 0, -Math.sin(yaw)); // body right
      _qa.setFromAxisAngle(_v2, 0.45 * gw); // face up, the brim out of the bottle's way
      worldRotate(Bn[B.Neck], _qa); worldRotate(Bn[B.Head], _qa);
      _ikT.copy(rig.mouth).applyMatrix4(Bn[B.Head].matrixWorld);
      chugDir(yaw, _v);
      _ikT.addScaledVector(_v, -(BOTTLE_H - BOTTLE_GRIP_Y)); // where the fist goes
      // the hand bone sits off the grip point: keep that offset
      _m.compose(a.grip.pos, a.grip.q, ONE);
      _ikP.setFromMatrixPosition(_m2.multiplyMatrices(Bn[a.grip.bone].matrixWorld, _m));
      Bn[B.RightHand].getWorldPosition(_ikH);
      _ikT.sub(_ikP).add(_ikH);
      _ikT.lerp(_ikH, 1 - gw);
      Bn[B.RightArm].getWorldPosition(_ikP).addScaledVector(_v2, 0.4).add(_ikUp.set(0, -0.35, 0));
      ik2(Bn[B.RightArm], Bn[B.RightForeArm], Bn[B.RightHand], _ikT, _ikP);
    }

    // the ditch: the fist's path after the last swallow (spike, over the shoulder, flip, mic drop)
    const dd = DITCH[this.ditchNow], fa = da - FIN_START;
    if (alive && dd.keys.length && fa >= 0 && fa < dd.end && rig.clip.chug && B.RightArm >= 0 && B.RightForeArm >= 0 && B.RightHand >= 0) {
      const w = smooth(clamp(fa / 0.1, 0, 1)) * (1 - smooth(clamp((fa - (dd.end - 0.2)) / 0.2, 0, 1)));
      const yaw = this.bodyYaw;
      _v2.set(Math.cos(yaw), 0, -Math.sin(yaw)); // body right
      _v.set(-Math.sin(yaw), 0, -Math.cos(yaw)); // body forward
      // the body sells it: bent into the spike, head back to follow the flip, a look at the bottle before the drop
      if (this.ditchNow === 2 && B.Spine1 >= 0) { _qa.setFromAxisAngle(_v2, -0.42 * bump(fa, 0.2, 0.36, 0.8)); worldRotate(Bn[B.Spine1], _qa); }
      if (this.ditchNow === 3 && B.Neck >= 0) { _qa.setFromAxisAngle(_v2, 0.5 * bump(fa, 0.25, 0.7, 1.1)); worldRotate(Bn[B.Neck], _qa); }
      if (this.ditchNow === 4 && B.Neck >= 0 && B.Head >= 0) { const k = bump(fa, 0.18, 0.4, 1.05); turnWorld(Bn[B.Neck], -0.55 * k); _qa.setFromAxisAngle(_v2, -0.2 * k); worldRotate(Bn[B.Head], _qa); }
      ditchKey(dd.keys, fa, _v3);
      Bn[B.RightArm].getWorldPosition(_ikT).addScaledVector(_v2, _v3.x).add(_ikUp.set(0, _v3.y, 0)).addScaledVector(_v, _v3.z);
      Bn[B.RightHand].getWorldPosition(_ikH);
      _ikT.lerp(_ikH, 1 - w);
      Bn[B.RightArm].getWorldPosition(_ikP).addScaledVector(_v2, 0.4).add(_ikUp.set(0, this.ditchNow === 1 ? 0.35 : -0.35, 0)); // the toss throws the elbow up
      ik2(Bn[B.RightArm], Bn[B.RightForeArm], Bn[B.RightHand], _ikT, _ikP);
    }

    // a long gun at the carry turns the chest toward it (left shoulder forward) so the support hand reaches the
    // foregrip; the neck takes the twist back out so the head keeps looking where the cowboy is going
    const twistW = hold.twoHand ? relax * (1 - oRoll) * (1 - stretch) * (1 - smooth(this.holster)) * (1 - this.sprintW * 0.6) : 0;
    if (twistW > 0.001 && B.Spine1 >= 0 && B.Spine2 >= 0 && B.Neck >= 0) {
      const ang = -0.42 * twistW;
      turnWorld(Bn[B.Spine1], ang * 0.5); turnWorld(Bn[B.Spine2], ang * 0.5); turnWorld(Bn[B.Neck], -ang);
      this.carryTwist = ang;
    } else this.carryTwist = 0;
    // ---- the pack: harnessed to the chest, so it turns with it (the chest is squared to the facing above, and
    // turns toward the aim when the feet stay planted), but it doesn't rock with the spine's side bend: it keeps
    // the lean and the turn and only a third of the roll
    if (B.Spine2 >= 0) {
      const sp = Bn[B.Spine2], pk = Bn[nB + 1];
      this.body!.getWorldQuaternion(_qa);
      sp.getWorldQuaternion(_q3);
      _q2.copy(_qa).invert().multiply(_q3).multiply(_iw.copy(a.bindWq[B.Spine2]).invert()); // model-frame delta from bind
      _e.setFromQuaternion(_q2, 'YXZ');
      const keep = 0.33 + 0.67 * oRoll; // a dive roll curls the whole body: the pack goes with it
      // the long-gun carry's chest turn happens under the harness: the pack stays square behind the hips
      _e.set(_e.x, _e.y - this.carryTwist, _e.z * keep, 'YXZ');
      _q2.setFromEuler(_e).multiply(a.bindWq[B.Spine2]);
      _q3.copy(_qa).multiply(_q2);
      setWorldQuat(pk, _q3, 1);
    }

    // ---- the gun. Aiming, the hand (from the aim clip / ADS IK) drives the gun, turned down the sight line.
    // Carrying, reloading and stowing, the GUN is placed (body frame, holds.ts) and the hands are IK'd onto it.
    const hand = Bn[a.grip.bone];
    _m.compose(a.grip.pos, a.grip.q, ONE);
    _m2.multiplyMatrices(hand.matrixWorld, _m);
    _m2.decompose(_v, _q, _s);
    const aimGun = aimW * (1 - oRoll) * (1 - stretch) * (1 - oMelee * 0.7) * (1 - relax) * (1 - spinW);
    if (aimGun > 0.001) {
      const flip = kick * (hold.twoHand ? 0.4 : 1.2);
      // the asset's gun points +Z: Ry(yaw + PI) * Rx(-pitch) puts it down the sight line
      _q2.setFromEuler(_e.set(-(clamp(c.pitch, -1.45, 1.45) - low * 1.6 + flip), c.yaw + Math.PI, 0, 'YXZ'));
      _q.slerp(_q2, aimGun);
    }
    const sc = a.height / 1.9;
    // pistols aimed from the hip: the aim clip leaves the hand left of centre, so the gun moves out under the sight
    // line and the hand is IK'd after it (below)
    const hipW = hold.pistolAim ? aimGun * (1 - this.adsW) * (1 - oReload) : 0; // aimGun carries the spin too
    if (hipW > 0.001) {
      _v3.set(0.14 * sc, 1.45 * sc, -0.45 * sc); this.group.localToWorld(_v3);
      hand.getWorldPosition(_v2);
      _v.addScaledVector(_v3.sub(_v2), hipW);
    }
    const carryW = relax * (1 - oRoll) * (1 - stretch) * (1 - spinW);
    if (carryW > 0.001) {
      // low ready (pistols level, long guns angled across the body), port arms in a sprint, a little run bounce
      const sw = this.sprintW, cp = hold.carry, sp = hold.sprint;
      const bob = Math.sin(this.runPhase * Math.PI * 4) * 0.02 * this.runAmt;
      _pose.x = cp.x + (sp.x - cp.x) * sw; _pose.y = cp.y + (sp.y - cp.y) * sw + bob; _pose.z = cp.z + (sp.z - cp.z) * sw;
      _pose.pitch = cp.pitch + (sp.pitch - cp.pitch) * sw + (hold.twoHand ? 0 : Math.sin(this.runPhase * Math.PI * 2) * 0.12 * sw);
      _pose.yaw = cp.yaw + (sp.yaw - cp.yaw) * sw; _pose.roll = cp.roll + (sp.roll - cp.roll) * sw;
      this.bodyPose(_pose, sc, _v2, _q2);
      _v.lerp(_v2, carryW); _q.slerp(_q2, carryW);
    }
    // reload: the gun goes to its reload pose and runs the weapon's own cycle (round-by-round loaders repeat it)
    let rf: ReturnType<typeof reloadFrame> | null = null;
    if (oReload > 0.001) {
      const cyc = reloadCycle(this.shown, a.pistol);
      rf = reloadFrame(hold.reload, (this.reloadT / cyc) % 1, this.muzLen);
      this.bodyPose(rf.gun, sc, _v2, _q2);
      _v.lerp(_v2, oReload); _q.slerp(_q2, oReload);
    }
    // stow: the gun arcs to its holster (thigh) or sling (back); the gun hand rides along most of the way
    const hol = smooth(this.holster);
    if (hol > 0.001) {
      const st = rig.stows[hold.stow];
      const hb = Bn[st.bone];
      _m.compose(st.pos, st.q, ONE);
      _m2.multiplyMatrices(hb.matrixWorld, _m);
      _m2.decompose(_v2, _q2, _s);
      _v.lerp(_v2, hol);
      _v3.set(0, (hold.stow === StowK.Back ? 0.25 : 0.12) * Math.sin(Math.PI * hol), 0);
      _v.add(_v3);
      _q.slerp(_q2, hol);
    }
    // world -> model space (the gun bone is a root bone under the body)
    _m.compose(_v, _q, ONE);
    _gw.copy(_m);
    _m2.copy(this.body!.matrixWorld).invert().multiply(_m);
    _m2.decompose(this.gun.position, this.gun.quaternion, _s);
    this.gun.updateMatrixWorld(true);
    // gun-local points use _gw: the gun bone's own matrix carries the 1e-4 hide-scale when a weapons-lane gun shows
    this.muzzleWorld.copy(a.muzzle).applyMatrix4(_gw);
    this.hasMuzzle = true;

    // ---- hands onto the gun
    // gun hand: wherever the gun was placed (carry / reload), and along the stow arc until the gun is seated
    const stowReach = this.holster < 0.999 ? Math.sin(Math.PI * Math.min(1, hol * 1.15)) : 0;
    const rightW = clamp(Math.max(carryW, oReload, stowReach, hipW) * (1 - oMelee) * (1 - oThrow) * (1 - oChug) * (1 - oRoll), 0, 1);
    if (rightW > 0.01 && B.RightArm >= 0 && B.RightForeArm >= 0 && B.RightHand >= 0) {
      // hand world = gun world * grip^-1
      _m.compose(a.grip.pos, a.grip.q, ONE).invert();
      _m2.multiplyMatrices(_gw, _m);
      _m2.decompose(_ikT, _q3, _s);
      Bn[B.RightHand].getWorldPosition(_ikH);
      _ikT.lerp(_ikH, 1 - rightW);
      _v2.set(Math.cos(this.bodyYaw), 0, -Math.sin(this.bodyYaw)); // body right
      // elbow down and a little back for a carry, out to the side otherwise
      _v.set(-Math.sin(this.bodyYaw), 0, -Math.cos(this.bodyYaw)); // body forward
      const cw = carryW / rightW;
      Bn[B.RightArm].getWorldPosition(_ikP).addScaledVector(_v2, 0.3 - 0.18 * cw).add(_ikUp.set(0, -0.55 - 0.05 * cw, 0)).addScaledVector(_v, -0.15 * cw);
      ik2(Bn[B.RightArm], Bn[B.RightForeArm], Bn[B.RightHand], _ikT, _ikP);
      setWorldQuat(Bn[B.RightHand], _q3, rightW);
      Bn[B.RightArm].updateMatrixWorld(true);
    }
    // Saloon lane: the right hand reaches for a world point (the hotdog off the plate, held, up to the mouth)
    const reachW = c.reachW ?? 0;
    if (((c.headDip ?? 0) > 0.001 || reachW > 0.01) && B.Head >= 0) {
      if ((c.headDip ?? 0) > 0.001) { this.rot(B.Head, X_AXIS, 0.22 * c.headDip!); Bn[B.Head].updateMatrixWorld(true); }
      if (reachW > 0.01 && B.RightArm >= 0 && B.RightForeArm >= 0 && B.RightHand >= 0) {
        Bn[B.RightHand].getWorldPosition(_ikH);
        _ikT.set(c.reachX ?? 0, c.reachY ?? 0, c.reachZ ?? 0).lerp(_ikH, 1 - Math.min(1, reachW));
        _v2.set(Math.cos(this.bodyYaw), 0, -Math.sin(this.bodyYaw)); // body right
        _v.set(-Math.sin(this.bodyYaw), 0, -Math.cos(this.bodyYaw)); // body forward
        // elbow down, out to the side and a little back
        Bn[B.RightArm].getWorldPosition(_ikP).addScaledVector(_v2, 0.3).add(_ikUp.set(0, -0.5, 0)).addScaledVector(_v, -0.1);
        ik2(Bn[B.RightArm], Bn[B.RightForeArm], Bn[B.RightHand], _ikT, _ikP);
        // the fingers close round what it's holding
        if (!this.rFingers) this.rFingers = a.bones.map((d, i) => (/RightHand(Index|Middle|Ring|Pinky)[1-3]$/.test(d.n) ? i : -1)).filter((i) => i >= 0);
        const fist = (c.fist ?? 0) * Math.min(1, reachW);
        for (const i of this.rFingers) this.rot(i, Z_AXIS, 1.25 * fist);
        Bn[B.RightArm].updateMatrixWorld(true);
      }
    }
    // support hand: the foregrip of a two-handed gun (aimed or carried), the reload's path, or the akimbo twin
    const twoW = hold.twoHand ? clamp(aimGun + carryW, 0, 1) * (1 - hol) * (1 - oReload) * (1 - oThrow) * (1 - oChug) * (1 - oMelee) : 0;
    const reloadL = rf && hold.reload !== ReloadK.Akimbo ? oReload * (1 - oMelee) * (1 - hol) : 0;
    const akL = hold.k === HoldK.Akimbo ? clamp(carryW + aimGun, 0, 1) * (1 - hol) * (1 - oMelee) * (1 - oThrow) * (1 - oChug) : 0;
    const leftW = Math.max(twoW, reloadL, akL);
    if (leftW > 0.01 && B.LeftArm >= 0 && B.LeftForeArm >= 0 && B.LeftHand >= 0) {
      const ml = this.muzLen;
      _ikT.set(0, -0.035, ml * hold.fore).applyMatrix4(_gw); // foregrip
      if (reloadL > 0.001 && rf) {
        if (rf.handGun) _v3.set(rf.hand.x, rf.hand.y, rf.hand.z).applyMatrix4(_gw);
        else { _v3.set(rf.hand.x * sc, rf.hand.y * sc, -rf.hand.z * sc); this.group.localToWorld(_v3); }
        _ikT.lerp(_v3, reloadL / leftW);
      }
      if (akL > 0.001) {
        // mirror of the gun hand across the body
        Bn[B.RightHand].getWorldPosition(_v3); this.group.worldToLocal(_v3); _v3.x = -_v3.x; this.group.localToWorld(_v3);
        _ikT.lerp(_v3, akL / leftW);
      }
      Bn[B.LeftHand].getWorldPosition(_ikH);
      _ikT.lerp(_ikH, 1 - leftW);
      // out of reach (a long foregrip from a standing chest): the collarbone swings the shoulder toward it
      if (B.LeftShoulder >= 0) {
        Bn[B.LeftArm].getWorldPosition(_v); Bn[B.LeftForeArm].getWorldPosition(_v2); Bn[B.LeftHand].getWorldPosition(_v3);
        const reachL = (_v.distanceTo(_v2) + _v2.distanceTo(_v3)) * 0.97, d = _v.distanceTo(_ikT);
        if (d > reachL) {
          Bn[B.LeftShoulder].getWorldPosition(_v2);
          _v3.subVectors(_v, _v2).normalize(); _v.subVectors(_ikT, _v2).normalize();
          const full = Math.acos(clamp(_v3.dot(_v), -1, 1));
          const want = Math.min(0.4, full, (d - reachL) / Math.max(0.05, _v2.distanceTo(_ikT)) * 1.2);
          if (full > 1e-4) {
            _q3.setFromUnitVectors(_v3, _v);
            Bn[B.LeftShoulder].getWorldQuaternion(_q);
            _q2.identity().slerp(_q3, want / full);
            setWorldQuat(Bn[B.LeftShoulder], _q.premultiply(_q2), 1);
          }
        }
      }
      _v2.set(Math.cos(this.bodyYaw), 0, -Math.sin(this.bodyYaw)); // body right
      Bn[B.LeftArm].getWorldPosition(_ikP).addScaledVector(_v2, -0.3).add(_ikUp.set(0, -0.5, 0));
      ik2(Bn[B.LeftArm], Bn[B.LeftForeArm], Bn[B.LeftHand], _ikT, _ikP);
      Bn[B.LeftArm].updateMatrixWorld(true);
    }
    // ---- weapons lane: the held weapon's placeholder rides the gun bone; the baked class gun hides
    if (gunHook && this.shown >= 0) {
      _m.compose(this.gun.position, this.gun.quaternion, ONE);
      _gw.multiplyMatrices(this.body!.matrixWorld, _m);
      // the twin's grip sits in the left palm: the left hand times the gun grip, mirrored across the hand
      let lh: THREE.Matrix4 | null = null;
      if (B.LeftHand >= 0 && hol < 0.3) {
        _m.compose(_v3.set(-a.grip.pos.x, a.grip.pos.y, a.grip.pos.z), _q.identity(), ONE);
        lh = _lhm.multiplyMatrices(Bn[B.LeftHand].matrixWorld, _m);
      }
      const cyc = reloadCycle(this.shown, a.pistol);
      _act.reload = oReload; _act.u = (this.reloadT / cyc) % 1; _act.n = Math.floor(this.reloadT / cyc); _act.shotAge = c.shotAge; _act.ads = this.adsW;
      const own = gunHook(c.id, this.shown, _gw, lh, this.muzzleWorld, this.group.visible && c.skyT < 0.5 && !c.holstered, _act); // saloon: guns away
      this.gun.scale.setScalar(own ? 1e-4 : 1);
      if (own) { _v2.setFromMatrixPosition(_gw); this.muzLen = clamp(_v2.distanceTo(this.muzzleWorld), 0.15, 1.6); }
    }

    // ---- heal lane: the whiskey bottle, out of the belt on the grab, drained in swallows, tossed after the finish
    this.whiskey(c, da, alive);

    // ---- melee lane: the class energy blade owns both arms while it's out (the gun is stowed by now)
    if (this.blade) this.blade.update(this, c, dt, time, alive, this.cloak, this.group.visible, camPos);

    // ---- LOD + materials
    const dist = Math.sqrt(camPos.distanceToSquared(this.group.position));
    const lod = dist > LOD_DIST ? 1 : 0;
    if (lod !== this.lod || this.fresh) {
      this.lod = lod;
      this.body!.geometry = lod ? a.geo1 : a.geo0;
      this.outline!.geometry = this.body!.geometry;
    }
    const cloaked = this.cloak > 0.02;
    const body = this.body!, ol = this.outline!;
    body.material = cloaked ? this.matCloak! : this.matSolid!;
    ol.material = cloaked ? this.olCloak : this.olSolid;
    ol.visible = quality > 0 || dist < 30;
    body.castShadow = !cloaked && quality > 0;
    if (cloaked) {
      this.matCloak!.opacity = 1 - 0.8 * this.cloak;
      this.olCloak.opacity = 0.75 - 0.2 * this.cloak;
      const u = this.matCloak!.userData.u;
      u.uShimmer.value = this.cloak; u.uCloakT.value = time;
    }
    const fireAmt = this.fire * (0.55 + 0.25 * Math.sin(time * 23) + 0.2 * Math.sin(time * 37 + 1));
    this.matSolid!.emissive.setHex(PAL.safetyOrange).multiplyScalar(fireAmt * 0.12);
    this.acid = damp(this.acid, c.acid ? 1 : 0, 8, dt);
    if (this.acid > 0.01) this.matSolid!.emissive.add(ACID_GLOW.clone().multiplyScalar(this.acid * (0.07 + 0.03 * Math.sin(time * 5)))); // acid-softened: a sick green sheen
    this.heatS = damp(this.heatS, c.heat ?? 0, 6, dt);
    { const u = this.matSolid!.userData.u; u.uPack.value = this.heatS; u.uFire.value = fireAmt; }
    const flick = stretch > 0.15 && (this.frame & 1) === 0;
    body.visible = !flick;
    ol.visible = ol.visible && !flick;

    // ---- jets: a 0..1 throttle from the sim's real thrust. CowboyRS carries no heat or throttle, so it is read
    // from what the thrust does: hover holds the body against gravity (vertical accel + g, vs the class lift);
    // flight is cruise thrust plus acceleration and climb, throttled back in a cooling dive; a boost/special or a
    // launch burst is full burner. Smoothed (fast spool-up, slower spool-down).
    const inv = dt > 0 ? 1 / dt : 0;
    this.ayS = damp(this.ayS, this.fresh ? 0 : clamp((c.vy - this.pvy) * inv, -60, 60), 6, dt);
    this.asS = damp(this.asS, this.fresh ? 0 : clamp((speed - this.psp) * inv, -60, 60), 6, dt);
    this.pvy = c.vy; this.psp = speed;
    let jt = 0;
    if (alive || c.skyT > 0) {
      if (c.move === 3) {
        jt = 0.66 + 0.2 * Math.min(1, speed / 24) + 0.14 * clamp(this.asS / 10, -1, 1) + 0.12 * clamp(c.vy / 8, 0, 1)
          - 0.32 * clamp((-c.vy - 5) / 10, 0, 1);
        jt = clamp(jt, 0.4, 1);
      } else if (c.move === 2) jt = clamp(0.3 + 0.32 * clamp(this.ayS / 18, -0.4, 1) + 0.06 * Math.min(1, h / 8), 0.18, 0.75);
      else if (c.move === 5 && (this.cls === 1 || this.cls === 3 || !c.grounded)) jt = 1;
      // the launch, a barrel roll and a hover flip all go to full burner
      if (this.launchAge < LAUNCH_DUR * 0.8 || (this.dodgeAir && this.dodgeSpin >= 2 && this.dodgeAge < this.dodgeDur * 0.75)) jt = 1;
      else if (!c.grounded && c.vy > 2 && c.move !== 4) jt = Math.min(1, c.vy / 13) * 0.95;
      if (c.skyT > 0) jt = Math.max(jt, 0.35 * c.skyT);
    }
    if (this.fresh || this.launchAge < dt * 1.5) this.jet = jt; // the launch lights at full burn, no spool
    this.jet = damp(this.jet, jt, jt > this.jet ? 14 : 5, dt);
    const heat = this.jet > 0.05 ? this.jet : 0;
    // the nozzle metal glows with the burn (body shader, swatch 6)
    const uS = this.matSolid!.userData.u, uC = this.matCloak!.userData.u;
    this.nozHeat = damp(this.nozHeat, heat, heat > this.nozHeat ? 3 : 1.2, dt);
    uS.uHeat.value = this.nozHeat; uC.uHeat.value = this.nozHeat;
    let fi = flameIdx;
    if (this.jet > 0.03 && !flick) {
      const pm = Bn[nB + 1].matrixWorld; // the pack bone
      const thr = this.jet;
      for (let n = 0; n < rig.nozzle.length && fi < MAX_FLAMES; n++) {
        const r0 = rig.nozR[n];
        // a little turbulence in the burn: length pulses, never in lockstep across nozzles
        const fl = 1 + 0.05 * Math.sin(time * 43 + n * 2.1 + this.id) + 0.04 * Math.sin(time * 71 + n) + 0.06 * thr * Math.sin(time * 9.3 + n * 1.7 + this.id);
        const len = (0.1 + 1.25 * Math.pow(thr, 1.6)) * Math.sqrt(r0 / 0.04) * fl * (1 + 0.15 * wF);
        const wid = r0 * (0.9 + 0.45 * thr) * (cloaked ? 0.6 : 1);
        _m2.multiplyMatrices(pm, rig.nozzle[n]);
        // the jet streams DOWN and BACK from the nozzle: its own axis bent toward world-down and the trailing
        // direction (against the velocity). In flight the nozzles face the chase camera, so without this the plume
        // would be seen end-on over the pack
        _v.set(-_m2.elements[4], -_m2.elements[5], -_m2.elements[6]).normalize();
        if (speed > 0.5) _v2.set(-c.vx / speed, -c.vy / speed, -c.vz / speed); else _v2.set(0, 0, 0);
        _v.multiplyScalar(1 - 0.7 * wF).addScaledVector(_v2, 0.45 * wF).addScaledVector(NEG_Y, 1.7 * wF + 0.1).normalize();
        _q.setFromUnitVectors(NEG_Y, _v);
        _v3.set(_m2.elements[12], _m2.elements[13], _m2.elements[14]);
        _v2.set(wid, len, wid);
        _m.compose(_v3, _q, _v2);
        flames.setMatrixAt(fi, _m);
        flames.setColorAt(fi, _col.setRGB(thr, cloaked ? 0.3 : 1, ((this.id * 0.37 + n * 0.29) % 1)));
        fi++;
      }
    }

    // ---- cloth
    const near = dist < 45;
    const iters = quality >= 2 && near ? 3 : quality >= 1 && near ? 2 : 1;
    const maxH = near ? 1 / 60 : 1 / 30;
    // the pack box in its own (bind model) frame: box->world = packBone * bind(Spine2)^-1
    if (B.Spine2 >= 0) {
      _m.compose(a.bindW[B.Spine2], a.bindWq[B.Spine2], ONE).invert();
      _m2.multiplyMatrices(Bn[nB + 1].matrixWorld, _m);
      this.packBox.set(_m2.elements, 16);
      _m2.invert();
      this.packBox.set(_m2.elements, 0);
      const pb = this.packBox, mn = a.pack.min, mx = a.pack.max;
      pb[32] = mn.x; pb[33] = mn.y; pb[34] = mn.z; pb[35] = mx.x; pb[36] = mx.y; pb[37] = mx.z;
    }
    let ns = 0;
    for (const s of rig.spheres) {
      _v.copy(s.pos).applyMatrix4(Bn[s.bone].matrixWorld);
      const o = ns * 4;
      this.spheres[o] = _v.x; this.spheres[o + 1] = _v.y; this.spheres[o + 2] = _v.z; this.spheres[o + 3] = s.r;
      ns++;
    }
    for (const rb of this.ribbons) {
      const bm = Bn[rb.def.bone].matrixWorld;
      const rm = rb.def.restBone !== undefined ? Bn[rb.def.restBone].matrixWorld : undefined;
      if (this.fresh || !rb.live) {
        _v.copy(rb.hang).transformDirection(bm);
        rb.reset(bm, _v, rm);
      } else {
        rb.setAnchors(bm);
        if (rm) rb.setRestFrame(rm);
        const dx = rb.a1[0] - rb.a0[0], dy = rb.a1[1] - rb.a0[1], dz = rb.a1[2] - rb.a0[2];
        const tp = Math.max(0.3, 45 * dt); // faster than 45 m/s is a teleport/snap, not motion
        if (dx * dx + dy * dy + dz * dz > tp * tp) { _v.copy(rb.hang).transformDirection(bm); rb.reset(bm, _v, rm); }
      }
      rb.step(Math.min(dt, 0.1), iters, maxH, WORLD_WIND.x, WORLD_WIND.y, WORLD_WIND.z, time, this.spheres, ns, this.packBox);
    }
    this.fresh = false;
    return fi;
  }
}
const _hipTmp = new Float32Array(4);
const _body = { clip: '', t: 0, w: 0 };
const _ikT = new THREE.Vector3(), _ikH = new THREE.Vector3(), _ikP = new THREE.Vector3(), _ikUp = new THREE.Vector3();
const _ia = new THREE.Vector3(), _ib = new THREE.Vector3(), _ic = new THREE.Vector3(), _ie = new THREE.Vector3(), _id = new THREE.Vector3(), _in = new THREE.Vector3();
const _ix = new THREE.Vector3();
const _iq = new THREE.Quaternion(), _iw = new THREE.Quaternion(), _ip = new THREE.Quaternion();
/** rotate `bone` in world space by `rot` (keeps its children attached), then refresh its subtree */
function worldRotate(bone: THREE.Bone, rot: THREE.Quaternion) {
  bone.getWorldQuaternion(_iw);
  _iw.premultiply(rot);
  (bone.parent as THREE.Object3D).getWorldQuaternion(_ip);
  bone.quaternion.copy(_ip.invert().multiply(_iw));
  bone.updateMatrixWorld(true);
}
/** blend a bone's world rotation toward `q` by w (parent-relative local written back) */
/** Turn a bone about world up by `ang` (radians), keeping the rest of its world rotation. */
function turnWorld(bone: THREE.Bone, ang: number) {
  bone.getWorldQuaternion(_iw);
  _iw.premultiply(_ip.setFromAxisAngle(UP_Y, ang));
  (bone.parent as THREE.Object3D).getWorldQuaternion(_ip);
  bone.quaternion.copy(_ip.invert().multiply(_iw));
  bone.updateMatrixWorld(true);
}
function setWorldQuat(bone: THREE.Bone, q: THREE.Quaternion, w: number) {
  bone.getWorldQuaternion(_iw);
  _iw.slerp(q, w);
  (bone.parent as THREE.Object3D).getWorldQuaternion(_ip);
  bone.quaternion.copy(_ip.invert().multiply(_iw));
  bone.updateMatrixWorld(true);
}
/** Two-bone IK: swing upper and lower so `end` reaches `target`, elbow bent toward `pole`. */
function ik2(upper: THREE.Bone, lower: THREE.Bone, end: THREE.Bone, target: THREE.Vector3, pole: THREE.Vector3) {
  upper.getWorldPosition(_ia); lower.getWorldPosition(_ib); end.getWorldPosition(_ic);
  const l1 = _ia.distanceTo(_ib), l2 = _ib.distanceTo(_ic);
  _id.subVectors(target, _ia);
  const d = clamp(_id.length(), 0.05, (l1 + l2) * 0.999);
  _id.normalize();
  const cosA = clamp((l1 * l1 + d * d - l2 * l2) / (2 * l1 * d), -1, 1);
  // the bend plane: target direction + the pole's component perpendicular to it
  _in.subVectors(pole, _ia); _in.addScaledVector(_id, -_in.dot(_id));
  if (_in.lengthSq() < 1e-8) _in.set(0, -1, 0);
  _in.normalize();
  _ie.copy(_ia).addScaledVector(_id, cosA * l1).addScaledVector(_in, Math.sqrt(1 - cosA * cosA) * l1); // wanted elbow
  _iq.setFromUnitVectors(_ib.sub(_ia).normalize(), _ix.copy(_ie).sub(_ia).normalize());
  worldRotate(upper, _iq);
  lower.getWorldPosition(_ib); end.getWorldPosition(_ic);
  _iq.setFromUnitVectors(_ic.sub(_ib).normalize(), _ie.copy(target).sub(_ib).normalize());
  worldRotate(lower, _iq);
}
// clip per 45-degree sector, starting forward and turning right (atan2(right, fwd))
const FIDGETS = ['fidget_crack_neck', 'fidget_idle_alt_1', 'fidget_cool_pose', 'fidget_neck_stretch', 'fidget_shoulder_roll', 'fidget_idle_alt_2', 'fidget_get_ready', 'fidget_happy_idle'];
const CWALK8 = ['cwalk_f', 'cwalk_fr', 'cwalk_r', 'cwalk_br', 'cwalk_b', 'cwalk_bl', 'cwalk_l', 'cwalk_fl'];
const RUN8 = ['run_f', 'run_fr', 'run_r', 'run_br', 'run_b', 'run_bl', 'run_l', 'run_fl'];
const WALK8 = ['walk_f', 'walk_fr', 'walk_r', 'walk_br', 'walk_b', 'walk_bl', 'walk_l', 'walk_fl'];

// ------------------------------------------------------------------ the view
const ACID_GLOW = new THREE.Color(PAL.grassGreen);

export class CowboyView implements CowboyViewApi {
  private readonly pool: Cowboy[] = [];
  /** lab debugging: every view registers itself (client/lab/cowboys.ts reads it from the browser pane) */
  static readonly all: CowboyView[] = [];
  private readonly byId = new Map<number, Cowboy>();
  private readonly cloth = new ClothBatch();
  private readonly flames: THREE.InstancedMesh;
  private readonly root = new THREE.Group();
  private layoutDirty = true;
  private readonly camPos = new THREE.Vector3();
  private readonly assets: (CowboyAsset | null)[] = [null, null, null, null];
  private disposed = false;

  /** weapons lane: per-weapon placeholder guns (client/weapons/guns.ts) */
  setGunHook(fn: GunHook | null) { gunHook = fn; }

  constructor(private ctx: ViewCtx) {
    CowboyView.all.push(this);
    this.root.name = 'cowboys';
    ctx.scene.add(this.root);
    this.root.add(this.cloth.mesh);
    const fm = plumeMaterial();
    this.flames = new THREE.InstancedMesh(flameGeometry(), fm, MAX_FLAMES);
    this.flames.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.flames.setColorAt(0, new THREE.Color(1, 1, 1));
    this.flames.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    this.flames.count = 0;
    this.flames.frustumCulled = false;
    this.flames.renderOrder = 2;
    this.flames.name = 'cowboy-jets';
    this.root.add(this.flames);
    this.tosses = new Tosses((x, z, y) => this.ground(x, z, y));
    this.root.add(this.tosses.root);
  }
  /** heal lane: thrown whiskey empties, and the game's shatter (FX + sound) when one meets the ground */
  /** the ground under a point (the game swaps in the current island's; the view's ctx keeps the first) */
  ground = (x: number, z: number, y: number) => this.ctx.world.support(x, z, y);
  /** the feet's ground probe: NaN where there is no world (labs, probes), never a throw mid-pose */
  private readonly footGround = (x: number, z: number, y: number) => { try { return this.ground(x, z, y); } catch { return NaN; } };
  readonly tosses: Tosses;
  set onBottleShatter(f: ((x: number, y: number, z: number, vx: number, vy: number, vz: number) => void) | null) { this.tosses.onShatter = f; }

  /** start fetching every class now (the game calls nothing else; first sync also triggers) */
  preload() { for (let c = 0; c < 4; c++) this.request(c); }

  /** asset for a class if loaded (lab / budgets) */
  assetFor(cls: number) { return this.assets[cls & 3]; }

  private request(cls: number) {
    const k = cls & 3;
    if (this.assets[k] || this.pending[k]) return;
    this.pending[k] = true;
    void loadCowboyAsset(k, this.ctx.quality).then((a) => {
      if (this.disposed) return;
      if (a) this.assets[k] = a;
      else setTimeout(() => { this.pending[k] = false; }, 5000); // retry later
    });
  }
  private readonly pending = [false, false, false, false];

  private acquire(c: CowboyRS): Cowboy | null {
    let cb = this.byId.get(c.id);
    if (!cb) {
      for (const p of this.pool) if (p.id < 0 || !this.isWanted(p.id)) { cb = p; break; }
      if (cb) { if (this.byId.get(cb.id) === cb) this.byId.delete(cb.id); }
      else { cb = new Cowboy(); this.pool.push(cb); }
      cb.id = c.id;
      cb.cls = -1;
      cb.fresh = true;
      this.byId.set(c.id, cb);
    }
    const k = c.cls & 3;
    if (cb.cls !== k || cb.slot !== c.slot || !cb.body) {
      const a = this.assets[k];
      if (!a) { this.request(k); return null; }
      cb.slot = c.slot;
      cb.build(a, k, c.slot, this.ctx.quality);
      if (!cb.group.parent) this.root.add(cb.group);
      if (!cb.bladeRoot.parent) this.root.add(cb.bladeRoot);
      cb.tosses = this.tosses;
      cb.feet.ground = this.footGround;
      cb.feet.reset();
      this.layoutDirty = true;
    }
    return cb;
  }
  private wanted: readonly CowboyRS[] = [];
  private wantedCount = 0;
  private isWanted(id: number) {
    for (let i = 0; i < this.wantedCount; i++) if (this.wanted[i].id === id) return true;
    return false;
  }

  sync(list: readonly CowboyRS[], count: number, dt: number, time: number): void {
    this.wanted = list; this.wantedCount = count;
    for (const p of this.pool) p.seen = false;
    this.ctx.camera.getWorldPosition(this.camPos);
    let fi = 0;
    for (let i = 0; i < count; i++) {
      const c = list[i];
      let cb: Cowboy | null = null;
      try { cb = this.acquire(c); } catch (e) { console.warn('[cowboys]', e); }
      if (!cb) continue;
      cb.seen = true;
      cb.group.visible = !c.hidden;
      cb.bladeRoot.visible = !c.hidden;
      fi = cb.update(c, dt, time, this.flames, fi, this.camPos, this.ctx.quality);
    }
    for (const p of this.pool) if (!p.seen) { p.group.visible = false; p.bladeRoot.visible = false; p.fresh = true; p.hasMuzzle = false; }
    if (this.layoutDirty) {
      const all: Ribbon[] = [];
      for (const p of this.pool) for (const r of p.ribbons) all.push(r);
      this.cloth.setRibbons(all);
      this.layoutDirty = false;
    }
    for (const p of this.pool)
      for (const r of p.ribbons) this.cloth.write(r, p.cloak, time, p.seen && p.group.visible);
    this.cloth.commit();
    this.tosses.update(dt);
    this.flames.count = fi;
    plumeT.value = time;
    this.flames.instanceMatrix.needsUpdate = true;
    if (this.flames.instanceColor) this.flames.instanceColor.needsUpdate = true;
  }

  /** lab: the gun bone's world barrel (+Z) and top (+Y) directions for a cowboy */
  debugGun(id: number) {
    const cb = this.byId.get(id);
    if (!cb || !cb.body) return '';
    const e = cb.gun.matrixWorld.elements;
    const hb = cb.bones[cb.rig!.a.grip.bone].matrixWorld.elements;
    const f = (x: number) => x.toFixed(2);
    return `gun z ${f(e[8])} ${f(e[9])} ${f(e[10])}  y ${f(e[4])} ${f(e[5])} ${f(e[6])}  hand y ${f(hb[4])} ${f(hb[5])} ${f(hb[6])}`;
  }

  /** saloon lane: a bone's world matrix for a cowboy, as drawn this frame (the hotdog rides the right hand) */
  boneWorld(id: number, name: 'RightHand' | 'RightForeArm' | 'LeftHand' | 'Head', out: THREE.Matrix4): boolean {
    const cb = this.byId.get(id);
    if (!cb || !cb.rig || !cb.group.visible) return false;
    const i = cb.rig.b[name];
    if (i === undefined || i < 0 || !cb.bones[i]) return false;
    out.copy(cb.bones[i].matrixWorld);
    return true;
  }

  /** the smoothed 0..1 jet throttle the plumes are drawn with (the sound follows the same number) */
  jetOf(id: number): number { return this.byId.get(id)?.jet ?? 0; }

  muzzle(id: number, out: THREE.Vector3): boolean {
    const cb = this.byId.get(id);
    if (!cb || !cb.hasMuzzle) return false;
    out.copy(cb.muzzleWorld);
    return true;
  }

  dispose(): void {
    this.disposed = true;
    for (const p of this.pool) p.dispose();
    this.pool.length = 0;
    this.byId.clear();
    this.cloth.dispose();
    this.tosses.dispose();
    this.flames.geometry.dispose();
    (this.flames.material as THREE.Material).dispose();
    this.root.removeFromParent();
  }
}
